import React, { useState, useEffect, useRef, useCallback } from 'react';
import LibraryApiCall from 'LibraryApiCall';
import config from 'config';
import ButtonLoader from 'LIBRARY/CommonResources/ButtonLoader/ButtonLoader';
import SuccessModal from 'LIBRARY/CommonResources/SuccessModal/SuccessModal';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import Select from 'react-select';

const isPolicyRecord = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

    return [
        'name',
        'description',
        'policy_name',
        'policyName',
        'stream_name',
        'NumberOfResources',
        'NumberOfDays',
        'PerDayFine',
        'ResourceDamagedCost',
        'NumberOfRenewalOfSameIssueResource',
        'ResourceLostCost',
        'iid',
        'dueDate',
        'formateddueDate',
    ].some((key) => Object.prototype.hasOwnProperty.call(value, key));
};

const isPolicyMap = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

    const entries = Object.entries(value);
    if (!entries.length) return false;

    return entries.every(([, item]) => item && typeof item === 'object' && !Array.isArray(item))
        && entries.some(([, item]) => isPolicyRecord(item));
};

export default function IssueReturnResource({ onCancel }) {
    // Local state
    const [assignedInstitutes, setAssignedInstitutes] = useState([]);
    const [linkedInstitutesArray, setLinkedInstitutesArray] = useState([]);
    const [selectedInstitute, setSelectedInstitute] = useState('all');
    const [userId, setUserId] = useState('');
    const [accessionNumber, setAccessionNumber] = useState('');

    // user details (section 2)
    const [userDetails, setUserDetails] = useState({
        erpId: '',
        name: '',
        issuedResources: 0,
        admissionNumber: '',
        rollNumber: '',
        email: '',
        mobileNo: '',
        photo: '',
        employeeId: '',
        userRole: '', // 'student' | 'faculty' | other — used to conditionally render fields
    });

    // issued resources table data (section 3)
    const [issuedList, setIssuedList] = useState([]);
    const [isIssuerLoading, setIsIssuerLoading] = useState(false);
    const [isUserLoading, setIsUserLoading] = useState(false);
    const [selectedIssuedIds, setSelectedIssuedIds] = useState([]);
    const [currentUserRole, setCurrentUserRole] = useState('');
    const [issuerUniqueId, setIssuerUniqueId] = useState('');
    const [issuerStudentIID, setIssuerStudentIID] = useState('');
    const [successModal, setSuccessModal] = useState(false);
    const [successModalType, setSuccessModalType] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [hasNoDueCertificate, setHasNoDueCertificate] = useState(false);
    const [noDueIssuedDate, setNoDueIssuedDate] = useState(null);
    const [membershipStatus, setMembershipStatus] = useState('NA');
    const [userAccess, setUserAccess] = useState('NA');
    const [streamName, setStreamName] = useState('');
    const [groupName, setGroupName] = useState('');
    const [academicYear, setAcademicYear] = useState('');
    // department access verification state
    const [departmentAccessVerified, setDepartmentAccessVerified] = useState(null); // null = unknown/not-checked, true = allowed, false = denied
    const [departmentAccessMessage, setDepartmentAccessMessage] = useState('');
    const [isUserAccessBlocked, setIsUserAccessBlocked] = useState(false);
    // confirmation modal for issuing No Due certificate
    const [showNoDueConfirm, setShowNoDueConfirm] = useState(false);
    // whether to show User Details / Resources / Action footer
    const [showSections, setShowSections] = useState(false);
    const [searchRole, setSearchRole] = useState('user'); // default to user search
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [isDepartmentLoading, setIsDepartmentLoading] = useState(false);
    // message shown when scanned entries indicate user not available
    const [scannedUserMessage, setScannedUserMessage] = useState('');
    // Accession select state
    const [accessionOptions, setAccessionOptions] = useState([]);
    const [Accession, setAccession] = useState(null);
    const [searchByAccession, setSearchByAccession] = useState('');
    const [loadingAccessionData, setLoadingAccessionData] = useState(false);
    // ref to the plain accession input element for cursor management
    const accessionInputRef = useRef(null);
    const accessionSuggestionsDebounceRef = useRef(null);
    // controls visibility of the accession suggestion dropdown
    const [showAccessionDropdown, setShowAccessionDropdown] = useState(false);
    // stores barcode + accessionSeriesId from the selected suggestion option
    const [selectedAccessionMeta, setSelectedAccessionMeta] = useState({ barcode: '', accessionSeriesId: '' });
    // message for resource availability (displayed before resource list table)
    const [resourceAvailabilityMessage, setResourceAvailabilityMessage] = useState('');
    // stores the parsed response from GetLibraryIssuerAndResourceDetails (mirrors IssuerAndResourceDetailsObj in legacy iframe code)
    const [issuerAndResourceDetails, setIssuerAndResourceDetails] = useState(null);
    // resource-search UI guard: show User Details only when issuerDetails exists
    const [isResourceSearchActive, setIsResourceSearchActive] = useState(false);
    const [resourceSearchHasIssuerDetails, setResourceSearchHasIssuerDetails] = useState(false);
    // Track if user was searched via ERP ID before accession search (controls userDetails update in accession flow)
    const [hasUserSearched, setHasUserSearched] = useState(false);

    const userDetailsInputRef = useRef(null);
    const userSuggestionsDebounceRef = useRef(null);
    const [searchByUserDetails, setSearchByUserDetails] = useState('');
    const [showUserDetailsDropdown, setShowUserDetailsDropdown] = useState(false);
    const [userOptions, setUserOptions] = useState([]);
    const [loadingUserData, setLoadingUserData] = useState(false);


    const getAccessionQueryValue = (inputValue = '') => {
        const rawValue = String(inputValue || '').trim();
        if (!rawValue) return '';

        return rawValue.split('|')[0].trim();
    };

    // const getAccessionQueryValue = (inputValue = '') => {
    //     const rawValue = String(inputValue || '')
    //         .replace(/[\r\n\t]+/g, ' ')
    //         .replace(/\s+/g, ' ')
    //         .trim();
    //     if (!rawValue) return '';

    //     const parts = rawValue
    //         .split('|')
    //         .map(part => part.replace(/\s+/g, ' ').trim())
    //         .filter(Boolean);

    //     if (parts.length <= 1) {
    //         return rawValue;
    //     }

    //     if (parts.length >= 3) {
    //         return parts[0];
    //     }

    //     const [firstPart, secondPart] = parts;
    //     const firstHasDigit = /\d/.test(firstPart);
    //     const secondHasDigit = /\d/.test(secondPart);

    //     if (!firstHasDigit && secondHasDigit) {
    //         return `${firstPart}${secondPart}`.replace(/\s+/g, ' ').trim();
    //     }

    //     return firstPart;
    // };

    const getUserQueryValue = (inputValue = '') => {
        const rawValue = String(inputValue || '').trim();
        if (!rawValue) return '';

        return rawValue.split('|')[0].trim();
    };

    const fetchAccessionSuggestions = async (inputValue) => {
        setLoadingAccessionData(true);

        const accessionQuery = getAccessionQueryValue(inputValue);

        const iidArray = (selectedInstitute === 'all') ? linkedInstitutesArray : [selectedInstitute];
        const iidValue = (selectedInstitute === 'all') ? config.institute_id : selectedInstitute;

        const payload = {
            iidArray: iidArray || [],
            iid: iidValue || config.institute_id,
            accessionSeries_number: accessionQuery,
            pageIndex: 1,
            pageLimit: 10,
        };

        try {
            const jsonResponse = await LibraryApiCall.makeApiCall(
                'library_getAllResourceSuggestions',
                payload,
                '/api/library/instructor.php'
            );

            if (jsonResponse?.expired) {
                setTimeout(() => fetchAccessionSuggestions(inputValue), 1000);
                return;
            }

            if (jsonResponse?.success) {
                const apiResult = jsonResponse.APIResult || jsonResponse;

                if (String(apiResult.status).toLowerCase() === 'fail') {
                    const noResultMsg = jsonResponse.message || apiResult.message || 'No resource suggestions found.';
                    setAccessionOptions([{ value: '', label: noResultMsg, disabled: true }]);
                    return;
                }

                const data = apiResult.data || apiResult || [];

                const opts = (Array.isArray(data) ? data : []).map(item => {
                    const accessionNum = item.accessionnumber || item.accession_number || item.accessionNumber || '';
                    const accessionSeries = item.accession_series || item.accessionSeries || item.series || '';
                    const title = item.title || item.name || '';
                    const instituteShort = item.instituteShortCode || item.institute_short_code || item.instituteShort || item.instituteShortCode || item.institute || item.institute_code || '';

                    const seriesPart = (accessionSeries && accessionNum) ? `${accessionSeries}${accessionNum}` : (accessionSeries ? accessionSeries : (accessionNum ? accessionNum : ''));
                    const parts = [seriesPart, title, instituteShort].filter(Boolean);
                    const label = parts.join(' | ');

                    const seriesValuePart = (accessionSeries && accessionNum) ? `${accessionSeries}${accessionNum}` : (accessionSeries ? accessionSeries : (accessionNum ? accessionNum : ''));
                    const partsValue = [seriesValuePart, title, instituteShort].filter(Boolean);

                    const value = partsValue.join(' | ');
                    return {
                        value: String(value),
                        label: String(label),
                        raw: item,
                        barcode: String(item.accessionNumber || item.accessionnumber || item.accession_number || ''),
                        accessionSeriesId: String(item.accession_series_id || item.accessionSeriesId || item.accessionSeries_id || ''),
                    };
                });

                setAccessionOptions(opts);
            } else {
                setAccessionOptions([]);
            }
        } catch (err) {
            console.error('Error fetching accession suggestions:', err);
            setAccessionOptions([]);
        } finally {
            setLoadingAccessionData(false);
        }
    };

    const clearAccessionSuggestionsDebounce = () => {
        if (accessionSuggestionsDebounceRef.current) {
            clearTimeout(accessionSuggestionsDebounceRef.current);
            accessionSuggestionsDebounceRef.current = null;
        }
    };

    const debouncedFetchAccessionSuggestions = (inputValue, delay = 1000) => {
        clearAccessionSuggestionsDebounce();

        const query = String(inputValue || '').trim();
        if (!query) {
            setLoadingAccessionData(false);
            return;
        }

        accessionSuggestionsDebounceRef.current = setTimeout(() => {
            fetchAccessionSuggestions(query);
            accessionSuggestionsDebounceRef.current = null;
        }, delay);
    };

    useEffect(() => {
        return () => {
            clearAccessionSuggestionsDebounce();
        };
    }, []);


    // fetch user suggestions for the user search input (section 1) based on input value and selected institute
    const fetchUserSuggestions = async (inputValue) => {
        setLoadingUserData(true);

        const userQuery = getUserQueryValue(inputValue);

        const iidArray = (selectedInstitute === 'all') ? linkedInstitutesArray : [selectedInstitute];
        const iidValue = (selectedInstitute === 'all') ? config.institute_id : selectedInstitute;

        const payload = {
            iidArray: iidArray || [],
            iid: iidValue || config.institute_id,
            filterUserSearch: userQuery,
            pageIndex: 1,
            pageLimit: 10,
        };

        try {
            const jsonResponse = await LibraryApiCall.makeApiCall(
                'library_getAllUserSuggestions',
                payload,
                '/api/library/instructor.php'
            );

            if (jsonResponse?.expired) {
                setTimeout(() => fetchUserSuggestions(inputValue), 1000);
                return;
            }

            if (jsonResponse?.success) {
                const apiResult = jsonResponse.APIResult || jsonResponse;
                const data = apiResult.data || apiResult || [];

                const opts = (Array.isArray(data) ? data : []).map(item => {
                    const salutation = String(item.salutation || '').trim();
                    const firstName = String(item.first_name || item.firstName || '').trim();
                    const lastName = String(item.last_name || item.lastName || '').trim();
                    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
                    const instituteName = String(item.institute_name || item.instituteName || item.institute || '').trim();
                    const roleRaw = String(item.user_role || item.userRole || '').trim();
                    const role = roleRaw ? `${roleRaw.charAt(0).toUpperCase()}${roleRaw.slice(1).toLowerCase()}` : '';
                    const erpId = String(item.erp_id || item.erpId || '').trim();

                    const label = [salutation, fullName, instituteName, role].filter(Boolean).join(' | ');
                    const value = erpId;

                    return { value: String(value), label: String(label) };
                });

                setUserOptions(opts);
            } else {
                setUserOptions([]);
            }
        } catch (err) {
            console.error('Error fetching user suggestions:', err);
            setUserOptions([]);
        } finally {
            setLoadingUserData(false);
        }
    };

    const clearUserSuggestionsDebounce = () => {
        if (userSuggestionsDebounceRef.current) {
            clearTimeout(userSuggestionsDebounceRef.current);
            userSuggestionsDebounceRef.current = null;
        }
    };

    const debouncedFetchUserSuggestions = (inputValue, delay = 1000) => {
        clearUserSuggestionsDebounce();

        const query = String(inputValue || '').trim();
        if (!query) {
            setLoadingUserData(false);
            return;
        }

        userSuggestionsDebounceRef.current = setTimeout(() => {
            fetchUserSuggestions(query);
            userSuggestionsDebounceRef.current = null;
        }, delay);
    };

    useEffect(() => {
        return () => {
            clearUserSuggestionsDebounce();
        };
    }, []);

    // Issue confirmation modal state (for reservation queue)
    const [showIssueConfirm, setShowIssueConfirm] = useState(false);
    const [issueConfirmationMessage, setIssueConfirmationMessage] = useState('');
    const [issueConfirmationQuestion, setIssueConfirmationQuestion] = useState('');
    const [issueConfirmationResource, setIssueConfirmationResource] = useState(null);
    // cache reservation responses by resource key (resourceCopyId or id)
    const [reservationCache, setReservationCache] = useState({});
    // institute/library rules (policy details) cache
    const [ruleList, setRuleList] = useState({});
    // cache for calculated due dates returned by libraryGetDueDateAsPerPolicy
    const [policyDueDates, setPolicyDueDates] = useState({});
    const [policyLimitErrors, setPolicyLimitErrors] = useState({});

    // shared input style: only bottom border
    const inputBaseStyle = {
        width: '100%',
        height: 40,
        padding: '0 8px',
        border: 'none',
        borderBottom: '1px solid #ccc',
        borderRadius: 0,
        outline: 'none',
    };

    const [collectedFineErrors, setCollectedFineErrors] = useState({});

    const getResourceRowKey = (resource = {}) => {
        const resourceCopyId = resource.resourceCopyId || resource.resource_copy_id || resource.copyId || resource.copy_id;
        if (resourceCopyId) return String(resourceCopyId);

        const issuedUniqueId = resource.issuedUniqueId || resource.issued_unique_id || resource.uniqueId || resource.issuedId || resource.issued_id;
        if (issuedUniqueId) return String(issuedUniqueId);

        if (resource.id) return String(resource.id);
        if (resource.accessionDetails) return String(resource.accessionDetails);
        if (resource.accessionNumber) return String(resource.accessionNumber);

        return '';
    };

    const getAccessionSearchRows = (rows = []) => {
        if (!Array.isArray(rows)) return [];
        return rows.filter(row => row?.resourceRowSource === 'accession');
    };

    const mergeIssuerRowsWithAccessionRows = (issuerRows = [], existingRows = []) => {
        const mergedRows = Array.isArray(issuerRows) ? issuerRows.slice() : [];
        const retainedAccessionRows = getAccessionSearchRows(existingRows);
        const existingKeys = new Set(mergedRows.map(getResourceRowKey).filter(Boolean));

        retainedAccessionRows.forEach((row) => {
            const key = getResourceRowKey(row);
            if (!key || !existingKeys.has(key)) {
                mergedRows.push(row);
            }
        });

        return mergedRows;
    };

    // Policy Rule by user iid
    const normalizeIidValue = (value) => String(value ?? '').trim();

    const getRolePoliciesByIid = useCallback((roleHint = '', iidHint = '') => {
        const roleKey = String(roleHint || '').toLowerCase().trim();
        const iidKey = normalizeIidValue(iidHint);

        if (!ruleList || typeof ruleList !== 'object') return {};

        const fromIidRole = iidKey && ruleList[iidKey] && typeof ruleList[iidKey] === 'object'
            ? ruleList[iidKey][roleKey]
            : null;
        if (fromIidRole && typeof fromIidRole === 'object' && !Array.isArray(fromIidRole) && isPolicyMap(fromIidRole)) {
            return fromIidRole;
        }

        const rolePolicies = roleKey && ruleList[roleKey] && typeof ruleList[roleKey] === 'object'
            ? ruleList[roleKey]
            : {};

        if (!rolePolicies || Array.isArray(rolePolicies)) return {};

        if (isPolicyMap(rolePolicies)) {
            if (!iidKey) return rolePolicies;

            const entries = Object.entries(rolePolicies);
            const filteredEntries = entries.filter(([, policy]) => {
                if (!policy || typeof policy !== 'object' || Array.isArray(policy)) return false;

                const policyIid = normalizeIidValue(policy.iid || policy.instituteId || policy.institute_id);
                const iidArray = policy.iidArray || policy.iid_array || policy.instituteIds || policy.institute_ids || [];

                if (Array.isArray(iidArray) && iidArray.length > 0) {
                    return iidArray.map(normalizeIidValue).includes(iidKey);
                }

                if (!policyIid) return false;
                return policyIid === iidKey;
            });

            return filteredEntries.length ? Object.fromEntries(filteredEntries) : {};
        }

        const fromRoleIid = iidKey && rolePolicies[iidKey] && typeof rolePolicies[iidKey] === 'object'
            ? rolePolicies[iidKey]
            : null;
        if (fromRoleIid && !Array.isArray(fromRoleIid) && isPolicyMap(fromRoleIid)) {
            return fromRoleIid;
        }

        if (!iidKey) return {};

        const entries = Object.entries(rolePolicies);
        if (!entries.length) return {};

        const hasIidMetadata = entries.some(([, policy]) => {
            if (!policy || typeof policy !== 'object' || Array.isArray(policy)) return false;
            return policy.iid != null
                || policy.instituteId != null
                || policy.institute_id != null
                || Array.isArray(policy.iidArray)
                || Array.isArray(policy.iid_array)
                || Array.isArray(policy.instituteIds)
                || Array.isArray(policy.institute_ids);
        });

        if (!hasIidMetadata) return {};

        const filteredEntries = entries.filter(([, policy]) => {
            if (!policy || typeof policy !== 'object' || Array.isArray(policy)) return false;

            const policyIid = normalizeIidValue(policy.iid || policy.instituteId || policy.institute_id);
            const iidArray = policy.iidArray || policy.iid_array || policy.instituteIds || policy.institute_ids || [];

            if (Array.isArray(iidArray) && iidArray.length > 0) {
                return iidArray.map(normalizeIidValue).includes(iidKey);
            }

            if (!policyIid) return false;
            return policyIid === iidKey;
        });

        return filteredEntries.length ? Object.fromEntries(filteredEntries) : {};
    }, [ruleList]);

    const buildPolicyOptions = useCallback((roleHint = '', iidHint = '') => {
        const policiesObj = getRolePoliciesByIid(roleHint, iidHint);
        if (!isPolicyMap(policiesObj)) return [];

        return Object.entries(policiesObj)
            .filter(([, v]) => isPolicyRecord(v))
            .map(([k, v]) => ({
            value: String(k),
            label: (v && (v.name || v.policy_name || v.policyName || v.stream_name || v.description || '')) || String(k),
            raw: v,
        }));
    }, [getRolePoliciesByIid]);

    // Fetch assigned institutes on mount and set default to 'all'
    useEffect(() => {
        fetchAssignedInstitutes();
        // document.getElementById("header").style.display = "none";
        // document.getElementById("navbarHeader").style.display = "none";
        // document.getElementById("footer").style.display = "none";
    }, []);

    // Fetch institute rules / policy details
    // Accept optional params so callers can pass freshly-fetched institute arrays
    // (avoids reading stale React state immediately after setState).
    const GetInstituteLibraryRulesHolidyCount = async (iidArrayParam = null, instituteParam = null) => {
        const resolvedInstitute = instituteParam || selectedInstitute || 'all';
        const iidArrayToUse = Array.isArray(iidArrayParam)
            ? iidArrayParam
            : (resolvedInstitute !== 'all' && resolvedInstitute
                ? [resolvedInstitute]
                : linkedInstitutesArray);

        const iidValue = resolvedInstitute === 'all'
            ? (iidArrayToUse && iidArrayToUse.length ? iidArrayToUse[0] : config.institute_id)
            : resolvedInstitute;

        const inputParamObject = {
            iidArray: iidArrayToUse,
            iid: iidValue,
        };

        try {
            const jsonResponse = await LibraryApiCall.makeApiCall(
                'GetInstituteLibraryRulesHolidyCount',
                inputParamObject,
                '/api/library/instructor.php'
            );

            if (jsonResponse?.expired) {
                setTimeout(() => GetInstituteLibraryRulesHolidyCount(iidArrayParam, instituteParam), 1000);
                return;
            }

            if (jsonResponse?.success) {
                const apiResult = jsonResponse.APIResult || jsonResponse;
                const data = apiResult.data || apiResult || {};
                setRuleList(data);
            } else {
                setRuleList({});
            }
        } catch (err) {
            console.error('Error fetching institute library rules:', err);
            setRuleList({});
        }
    };

    const fetchAssignedInstitutes = async () => {
        try {
            const inputParamObject = { facultyId: config.faculty_id || '' };

            const jsonResponse = await LibraryApiCall.makeApiCall(
                'library_GetFacultyAssignedInstituteList',
                inputParamObject,
                '/api/library/libraryLinkInstitute.php'
            );

            if (jsonResponse?.expired) {
                setTimeout(fetchAssignedInstitutes, 1000);
                return;
            }

            if (jsonResponse?.success) {
                const rawData = jsonResponse.APIResult;
                setLinkedInstitutesArray(rawData.linkedIidArray || []);
                setAssignedInstitutes(rawData.data);
                // pass the freshly fetched linkedIidArray so the rules call
                // doesn't rely on React state that hasn't updated yet
                GetInstituteLibraryRulesHolidyCount(rawData.linkedIidArray || []);
            }
        } catch (error) {
            console.error('Error fetching institutes:', error);
        }
    };

    // Search user: call scanned entries API then issuer details API
    const handleSearchUser = async (searchUserId) => {
        resetUserState({ preserveAccessionResults: true });
        setIsResourceSearchActive(false);
        setResourceSearchHasIssuerDetails(false);
        fetchScannedEntries(searchUserId);
    };



    const GetMultipleInstituteStreamsObject = async (instituteValue = selectedInstitute) => {
        setIsDepartmentLoading(true);
        const inputParamObject = {
            iidArray: instituteValue === "all" ? linkedInstitutesArray : [instituteValue],
        };

        try {
            let jsonResponse = await LibraryApiCall.makeApiCall(
                'GetMultipleInstituteStreamsObject',
                inputParamObject,
                "/api/admission/streamManagement.php"
            );

            if (jsonResponse.expired) {
                // keep loading true and retry shortly
                setTimeout(() => {
                    GetMultipleInstituteStreamsObject();
                }, 1000);
                return;
            }

            if (jsonResponse.success) {
                let apiResult = jsonResponse.APIResult || jsonResponse;
                const streamData = apiResult.data || {};

                const formattedOptions = Object.entries(streamData).map(([key, value]) => ({
                    value: key,
                    label: `${value.stream_name.trim()} | ${value.instituteShortCode}`
                }));

                setDepartmentOptions(formattedOptions);
            } else {
                setDepartmentOptions([]);
            }
        } catch (err) {
            console.error('Error fetching streams for institutes:', err);
            setDepartmentOptions([]);
        } finally {
            setIsDepartmentLoading(false);
        }
    }
    // (Removed unused helper: accession suggestion helper lived here.)

    // Reset user-related UI/state when inputs change
    const resetUserState = ({ preserveAccessionResults = false } = {}) => {
        const retainedAccessionRows = preserveAccessionResults ? getAccessionSearchRows(issuedList) : [];
        const retainedAccessionRowKeys = new Set(retainedAccessionRows.map(getResourceRowKey).filter(Boolean));

        setUserDetails({ erpId: '', name: '', issuedResources: 0, admissionNumber: '', rollNumber: '', email: '', mobileNo: '', employeeId: '', userRole: '' });
        setUserAccess('NA');
        setIssuedList(retainedAccessionRows);
        setSelectedIssuedIds(preserveAccessionResults ? selectedIssuedIds.filter(id => retainedAccessionRowKeys.has(String(id))) : []);
        setIssuerStudentIID('');
        setIsUserLoading(false);
        setIsIssuerLoading(false);
        setHasNoDueCertificate(false);
        setNoDueIssuedDate(null);
        setMembershipStatus('NA');
        setStreamName('');
        setGroupName('');
        setAcademicYear('');
        setIsUserAccessBlocked(false);
        setDepartmentAccessVerified(null);
        setDepartmentAccessMessage('');
        setShowSections(retainedAccessionRows.length > 0);
        setSelectedDepartment('');
        setScannedUserMessage('');
        setIsResourceSearchActive(false);
        setResourceSearchHasIssuerDetails(false);
    };

    const fetchScannedEntries = async (searchUserId) => {
        const preservedAccessionRows = getAccessionSearchRows(issuedList);
        const hasPreservedAccessionRows = preservedAccessionRows.length > 0;

        // mark that sections should be shown while fetching/searching
        setShowSections(true);
        // clear any previous scanned-user message
        setScannedUserMessage('');
        setIsUserLoading(true);

        const inputParamObject = {
            iid: config.institute_id,
            iidArray: selectedInstitute === "all" ? linkedInstitutesArray : [selectedInstitute],
            entriesSearchForm: { "user_id": searchUserId }
        }

        let jsonResponse = await LibraryApiCall.makeApiCall(
            'library_getScannedEntries',
            inputParamObject,
            "/api/library/library.php"
        );

        if (jsonResponse.expired) {
            setTimeout(() => {
                fetchScannedEntries(searchUserId);
            }, 1000);
        } else {

            if (jsonResponse.success) {
                // successful response (may or may not contain a user)
                let apiResult = jsonResponse.APIResult || jsonResponse;

                // Special-case: API can return { data: [], sortedIdArray: [], success: "true", user: "User not available" }
                // When that happens, show a simple "User not available" message and do NOT call downstream APIs.
                if (apiResult && (apiResult.user === 'User not available' || String(apiResult.user || '').toLowerCase().includes('user not available'))) {
                    setIsUserAccessBlocked(false);
                    setUserAccess('NA');
                    setScannedUserMessage('User not available');
                    setShowSections(hasPreservedAccessionRows);
                    setIsUserLoading(false);
                    return;
                }

                // keep sections visible for other responses
                setShowSections(true);

                // Normalize data location (some APIs return array, others return object keyed by id)
                const rawData = apiResult.data || apiResult;
                let userRecord = null;

                if (Array.isArray(rawData) && rawData.length) {
                    // student-style response (array)
                    userRecord = rawData[0];
                } else if (rawData && typeof rawData === 'object') {
                    // object keyed by id (faculty-style) or an object containing user keys
                    const vals = Object.values(rawData).filter(v => v && typeof v === 'object');
                    if (vals.length) userRecord = vals[0];
                    else userRecord = rawData;
                }

                // If we found a user record, map fields based on role
                if (userRecord) {
                    const scannedUserAccess = userRecord.access
                        || userRecord.Access
                        || 'NA';
                    setUserAccess(String(scannedUserAccess || 'NA'));
                    setIsUserAccessBlocked(String(scannedUserAccess || '').toLowerCase() === 'blocked');

                    const roleRaw = (userRecord.Role || userRecord.role || '').toString().toLowerCase();
                    if (roleRaw === 'student') {
                        // student-shaped response
                        setUserDetails({
                            erpId: userRecord.student_user_id || userRecord.userid || userRecord.userId || '',
                            name: userRecord.studentFirstName ? `${userRecord.studentFirstName}${userRecord.studentLastName ? ' ' + userRecord.studentLastName : ''}` : (userRecord.name || ''),
                            issuedResources: 0,
                            admissionNumber: userRecord.PRN || userRecord.universityNumber || '',
                            rollNumber: userRecord.rollNumber || '',
                            email: userRecord.mail || userRecord.Email || '',
                            mobileNo: userRecord.mobileNo || userRecord.Mobile || '',
                            photo: userRecord.profilePhoto || userRecord.profileImage || '',
                            employeeId: '',
                            userRole: 'student',
                        });
                    } else {
                        // assume employee/faculty-shaped response
                        setUserDetails({
                            erpId: userRecord.UserId || userRecord.userid || userRecord.userId || '',
                            name: userRecord.employeeName ? `${userRecord.employeeName}${userRecord.employeeLastName ? ' ' + userRecord.employeeLastName : ''}` : (userRecord.name || ''),
                            issuedResources: 0,
                            admissionNumber: '',
                            email: userRecord.Email || userRecord.mail || '',
                            mobileNo: userRecord.mobileNo || userRecord.Mobile || '',
                            photo: userRecord.profilePhoto || userRecord.profileImage || '',
                            employeeId: userRecord.employeeId || userRecord.employee_id || userRecord.empId || userRecord.emp_id || '',
                            userRole: roleRaw || 'faculty',
                        });
                    }

                    // prepare parameters for issuer details API
                    const userIdParam = userRecord.student_user_id || userRecord.userid || userRecord.UserId || userRecord.userId || '';
                    const roleParamLocal = (userRecord.Role || userRecord.role || '').toString().toLowerCase();
                    setCurrentUserRole(roleParamLocal);
                    const erpIdParam = userRecord.UserId || userRecord.student_user_id || "";
                    const iidParam = userRecord.InstituteId
                        || userRecord.instituteId
                        || userRecord.institute_id
                        || userRecord.iid
                        || ((selectedInstitute === 'all')
                            ? config.institute_id
                            : selectedInstitute);

                    // call issuer details API with extracted params
                    await getUserNoDueCertificateDetails(userIdParam, roleParamLocal, erpIdParam, iidParam);
                } else {
                    setUserAccess('NA');
                    setIsUserAccessBlocked(false);
                }
                setIsUserLoading(false);
            } else {
                setUserDetails({
                    erpId: '',
                    name: '',
                    issuedResources: 0,
                    admissionNumber: '',
                    email: '',
                    mobileNo: '',
                });
                setUserAccess('NA');
                setIsUserAccessBlocked(false);
                setShowSections(hasPreservedAccessionRows);
                setIsUserLoading(false);
            };
        }
    };


    const getUserNoDueCertificateDetails = async (userIdParam, roleParam, erpIdParam, iidParam) => {
        let inputParamObject = {
            iid: iidParam
                || ((selectedInstitute === 'all')
                    ? config.institute_id
                    : selectedInstitute)
                || config.institute_id,
            userId: userIdParam || '',
        };

        let jsonResponse = await LibraryApiCall.makeApiCall(
            'library_GetLibraryNoDueCertificate',
            inputParamObject,
            "/api/library/libraryNoDueCertificate.php"
        );

        if (jsonResponse?.expired) {
            setTimeout(() => {
                getUserNoDueCertificateDetails(userIdParam, roleParam, erpIdParam, iidParam);
            }, 1000);
            return;
        }

        if (jsonResponse?.success) {
            const apiResult = jsonResponse.APIResult || jsonResponse;

            // normalize data location
            const raw = apiResult.data || apiResult || {};

            let isEmpty = false;
            if (Array.isArray(raw)) {
                isEmpty = raw.length === 0;
            } else if (raw && typeof raw === 'object') {
                isEmpty = Object.keys(raw).length === 0;
            } else {
                isEmpty = true;
            }

            if (isEmpty) {
                // No existing No-Due Certificate: allow issuing -> still fetch issuer details
                setHasNoDueCertificate(false);
                setNoDueIssuedDate(null);
                // fetch issued resources and user details
                await GetLibraryResourceIssuerDetails(userIdParam, roleParam, erpIdParam, iidParam);
            } else {
                // There is an existing No Due Certificate
                setHasNoDueCertificate(true);

                // try to extract issuedOn from first record
                let firstKey = Array.isArray(raw) ? 0 : Object.keys(raw)[0];
                let issuedOn = '';
                if (Array.isArray(raw) && raw.length && raw[0].issuedOn) {
                    issuedOn = raw[0].issuedOn;
                } else if (raw && raw[firstKey] && raw[firstKey].issuedOn) {
                    issuedOn = raw[firstKey].issuedOn;
                }

                if (issuedOn) {
                    const d = new Date(issuedOn);
                    if (!isNaN(d.getTime())) {
                        setNoDueIssuedDate(d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }));
                    }
                }

                // still fetch issued resources for return/renewal view
                await GetLibraryResourceIssuerDetails(userIdParam, roleParam, erpIdParam, iidParam);
            }
        } else {
            // on failure, clear flag and still attempt to fetch issuer details
            setHasNoDueCertificate(false);
            setNoDueIssuedDate(null);
            await GetLibraryResourceIssuerDetails(userIdParam, roleParam, erpIdParam, iidParam);
        }
    };

    const GetLibraryResourceIssuerDetails = async (userIdParam, roleParam, erpIdParam, iidParam) => {
        setIsIssuerLoading(true)

        let inputParamObject = {
            iidArray: linkedInstitutesArray,
            iid: selectedInstitute === "all" ? iidParam : selectedInstitute,
            // Prefer the explicit userIdParam passed by the caller for department searches
            // because React state updates for `selectedDepartment` may be asynchronous.
            userId: searchRole === 'department' ? (userIdParam || selectedDepartment) : (userIdParam || ''),
            role: searchRole === 'department' ? 'department' : (roleParam || ''),
            userRegistrationKey: ''
        };

        let jsonResponse = await LibraryApiCall.makeApiCall(
            'GetLibraryResourceIssuerDetails',
            inputParamObject,
            "/api/library/instructor.php"
        );

        if (jsonResponse?.expired) {
            setTimeout(() => {
                GetLibraryResourceIssuerDetails(userIdParam, roleParam, erpIdParam, iidParam);
            }, 1000);
            return;
        }

        if (jsonResponse?.success) {
            const apiResult = jsonResponse.APIResult || jsonResponse;

            // Normalize issuedResources (object keyed by id) and order with sortedIdArray when provided
            const issuedResourcesObj = apiResult.issuedResources || apiResult.data?.issuedResources || {};
            const sortedIdArray = apiResult.sortedIdArray || Object.keys(issuedResourcesObj || {});

            const mappedList = (Array.isArray(sortedIdArray) ? sortedIdArray : Object.keys(issuedResourcesObj || {})).map((key) => {
                const r = issuedResourcesObj[key] || {};
                // resource copy id (the copy of the resource)
                const resourceCopyIdVal = r.resource_copy_id || r.resourceCopyId || r.resource_copy || r.copyId || r.copy_id || null;
                // issued-unique id (the issued record unique identifier)
                const issuedUid = r.issued_unique_id || r.issuedUniqueId || r.uniqueId || r.issuedId || r.issued_id || '';
                const damaged = Number(r.damagedPrice || r.damaged_price || 0) || 0;
                const lost = Number(r.lostPrice || r.lost_price || 0) || 0;
                const late = Number(r.lateFine ?? r.fine_amount ?? 0) || 0;

                return {
                    id: key,
                    issuedUniqueId: key,
                    userUniqueId: issuedUid || (r.user_id || r.userId || r.userid || ''),
                    // role of the searched user (used by UI/logic)
                    selectedUserRole: roleParam || currentUserRole || '',
                    userInstituteId: String(iidParam || r.studentIID || r.student_iid || r.iid || r.instituteId || r.institute_id || ''),
                    resourceCopyId: resourceCopyIdVal,
                    // preserve raw accession fields and compute display in JSX
                    accessionSeries: r.accession_series || r.accessionSeries || r.series || '',
                    accessionNumber: r.accessionnumber || r.accession_number || r.accessionNumber || '',
                    resourceInstituteName: r.resourseInstituteName || r.resourceInstituteName || r.instituteName || r.institute || '',
                    accessionDetails: r.accessionnumber || r.accession_number || '-',
                    // department id for verification
                    resourceDepartmentId: r.department_id || r.departmentId || r.insti_stream_id || r.sid || r.default_department_id || null,
                    policy_id: r.policy_id || "",
                    bookStatus: r.status || r.bookStatus || '-',
                    title: r.title || '-',
                    issueDate: r.issued_date || r.issueDate || '',
                    dueDate: r.dueDate || r.due_date || '',
                    type: r.resourcePeriodicalType || r.type || '-',
                    lateFine: late,
                    // default resource condition to 'Good' when not provided
                    resourceCondition: r.resourceCondition || r.resource_condition || 'Good',
                    // keep individual damaged/lost values so the UI can choose one when condition changes
                    damagedPrice: damaged,
                    lostPrice: lost,
                    // Initialize damaged/lost fine to 0 (assume 'Good' condition by default)
                    damagedLostFine: 0,
                    // totalFine initially includes only late fine; damaged/lost will be added when changed
                    totalFine: late,
                    collectedFine: r.collected_fine || r.collectedFine || 0,
                    receiptNumber: r.receiptNumber || r.receipt_number || '-',
                };
            });

            // attach policy options and default-selected policy per user role
            const roleKeyRaw = (roleParam || currentUserRole || '').toString().toLowerCase();
            const fallbackUserIid = normalizeIidValue(
                iidParam
                || apiResult?.data?.studentIID
                || apiResult?.data?.studentIid
                || apiResult?.data?.student_iid
                || apiResult?.data?.iid
                || apiResult?.data?.instituteId
                || apiResult?.data?.institute_id
                || ((selectedInstitute === 'all') ? config.institute_id : selectedInstitute)
                || config.institute_id
            );

            const mappedWithPolicies = mappedList.map(item => {
                const itemUserIid = normalizeIidValue(item.userInstituteId || fallbackUserIid);
                const localPolicyOptions = buildPolicyOptions(roleKeyRaw, itemUserIid);
                // prefer existing policy_id from item, otherwise default to first policy for the role
                const existingPolicy = item.policy_id || item.policyId || '';
                const firstPolicy = (localPolicyOptions && localPolicyOptions.length) ? localPolicyOptions[0].value : '';
                const selectedPolicyId = existingPolicy ? String(existingPolicy) : firstPolicy;

                return {
                    ...item,
                    userInstituteId: itemUserIid,
                    policyOptions: localPolicyOptions,
                    selectedPolicyId,
                    // ensure policy_id exists so later policy-based due-date calc can pick it up
                    policy_id: existingPolicy ? String(existingPolicy) : (firstPolicy || ''),
                    resourceRowSource: 'issuer',
                };
            });

            // Merge current issuer rows with any accession-searched rows that were already added.
            setIssuedList(prev => mergeIssuerRowsWithAccessionRows(mappedWithPolicies, prev));

            // For available resources with a policy assigned, request calculated due dates
            // Do not block UI; getPolicyWiseDueDate will update rows and we also populate policyDueDates
            try {
                mappedWithPolicies.forEach(item => {
                    const st = String(item.bookStatus || '').toLowerCase();
                    const pid = item.policy_id || item.selectedPolicyId || '';
                    const rcid = item.resourceCopyId || item.id || '';
                    if (st === 'available' && pid && rcid) {
                        getPolicyWiseDueDate(String(pid), rcid)
                            .then(calculated => {
                                if (calculated) {
                                    setPolicyDueDates(prev => ({ ...prev, [String(rcid)]: calculated }));
                                }
                            })
                            .catch(e => console.error('policy due date fetch failed', e));
                    }
                });
            } catch (e) {
                console.error('Error scheduling due-date fetch for available resources', e);
            }

            // merge/override user details from APIResult.data when available
            if (apiResult.data) {
                const d = apiResult.data;
                // Determine role from API response so we can decide which fields to surface
                const roleFromApi = (d.role || d.user_type || d.roleType || roleParam || '').toString().toLowerCase();
                const isStudentFromApi = roleFromApi === 'student';
                setUserDetails(prev => ({
                    ...prev,
                    erpId: d.user_text_id || d.studentid || prev.erpId,
                    name: d.name || prev.name,
                    issuedResources: d.TotalIssuedResources != null ? d.TotalIssuedResources : (mappedList.length || prev.issuedResources),
                    admissionNumber: isStudentFromApi ? (d.admissionNumber || d.admission_number || prev.admissionNumber) : '',
                    rollNumber: isStudentFromApi ? (d.rollNumber  || d?.defaultStreamGroupData?.rollNumber) : '',
                    email: d.student_email_id || d.student_email || d.email || prev.email,
                    photo: d.profilePhoto || d.profileImage || prev.photo,
                    employeeId: !isStudentFromApi ? (d.employeeId || d.employee_id || d.empId || d.emp_id || prev.employeeId || '') : '',
                    userRole: roleFromApi || prev.userRole,
                }));

                // Prefer several possible user-id fields returned by API
                const candidate = d.userid || d.userId || d.UserId || d.user_id || d.user_unique_id || '';
                setIssuerUniqueId(candidate);

                // Persist the issuer institute id from API for No Due certificate payload
                const studentIidCandidate = d.studentIID || d.studentIid || d.student_iid || '';
                setIssuerStudentIID(studentIidCandidate ? String(studentIidCandidate) : '');

                // compute membership status per provided logic
                try {
                    const IssuerDetailsObj = apiResult.data || {};
                    let status = 'NA';
                    const valid_from = IssuerDetailsObj['valid_from'];
                    const valid_to = IssuerDetailsObj['valid_to'];

                    if (valid_from && valid_to && valid_from !== '' && valid_to !== '') {
                        const validTill = moment(new Date(valid_to)).startOf('day');
                        const today = moment(new Date()).startOf('day');

                        if (validTill.isBefore(today)) {
                            status = 'Expired';
                        } else {
                            status = 'Active';
                        }
                    }

                    setMembershipStatus(status);

                    // pull stream and group names from defaultStreamGroupData
                    const dsg = IssuerDetailsObj.defaultStreamGroupData || IssuerDetailsObj.default_stream_group_data || IssuerDetailsObj.defaultStreamGroupData || {};
                    setStreamName(dsg.streamName || dsg.stream_name || dsg.stream || '');
                    setGroupName(dsg.groupname || dsg.group_name || dsg.group || '');

                    // pull academic year
                    const acaYear = dsg.yearname  || '';
                    setAcademicYear(String(acaYear || ''));

                    // ---- Department access verification (ported from iframe) ----
                    // Determine parameters for verification
                    const iidValueForSelectedRow = iidParam || (selectedInstitute === 'all' ? config.institute_id : selectedInstitute);
                    const facultyId = config.faculty_id || '';

                    // derive issuerDefaultDepartmentId as an array depending on role (student vs faculty)
                    let issuerDefaultDepartmentId = [];
                    try {
                        const roleForIssuer = (IssuerDetailsObj.role || IssuerDetailsObj.user_type || IssuerDetailsObj.roleType || '').toString().toLowerCase();
                        if (roleForIssuer === 'student') {
                            const defaultStreamGroupData = IssuerDetailsObj.defaultStreamGroupData || IssuerDetailsObj.default_stream_group_data || {};
                            if (defaultStreamGroupData && (defaultStreamGroupData.sid || defaultStreamGroupData.sId || defaultStreamGroupData.streamId)) {
                                issuerDefaultDepartmentId = [defaultStreamGroupData.sid || defaultStreamGroupData.sId || defaultStreamGroupData.streamId];
                            }
                        } else {
                            const facultydefaultStreamGroupData = IssuerDetailsObj.facultydefaultStreamGroupData || IssuerDetailsObj.faculty_default_stream_group_data || {};
                            if (facultydefaultStreamGroupData && (facultydefaultStreamGroupData.insti_stream_id || facultydefaultStreamGroupData.instiStreamId)) {
                                issuerDefaultDepartmentId = [facultydefaultStreamGroupData.insti_stream_id || facultydefaultStreamGroupData.instiStreamId];
                            }
                        }
                    } catch (e) {
                        issuerDefaultDepartmentId = [];
                    }

                    // call department access verification API
                    try {
                        const verifyInput = { iid: iidValueForSelectedRow, facultyID: facultyId, issuerResourcesDepartmentID: issuerDefaultDepartmentId };
                        const verifyResp = await LibraryApiCall.makeApiCall(
                            'library_GetDepartmentAccessVerification',
                            verifyInput,
                            "/api/library/libraryDepartmentWiseResponsible.php"
                        );

                        if (verifyResp?.expired) {
                            // try again shortly
                            setTimeout(() => {
                                GetLibraryResourceIssuerDetails(userIdParam, roleParam, erpIdParam, iidParam);
                            }, 1000);
                            return;
                        }

                        // parse response - APIResult may be stringified JSON
                        let verifyResult = verifyResp.APIResult || verifyResp;
                        if (typeof verifyResult === 'string') {
                            try { verifyResult = JSON.parse(verifyResult); } catch (e) { /* ignore parse error */ }
                        }

                        if (verifyResult && verifyResult.errorCode !== '403') {
                            const deptVerified = !!verifyResult.departmentAccessVerified;
                            setDepartmentAccessVerified(deptVerified);

                            if (deptVerified) {
                                const blockedAccess = String(IssuerDetailsObj['access'] || '').toLowerCase() === 'blocked';
                                setIsUserAccessBlocked(blockedAccess);
                                setDepartmentAccessMessage(blockedAccess ? `The resources cannot be issued to ${IssuerDetailsObj['name'] || 'this user'} because access is blocked.` : '');
                            } else {
                                setIsUserAccessBlocked(false);
                                setDepartmentAccessMessage(`Your assigned branch/department in the library does not match the selected issuer's default branch.`);
                            }
                        } else {
                            // treat as not verified (or API returned 403) and hide forms
                            setIsUserAccessBlocked(false);
                            setDepartmentAccessVerified(false);
                            setDepartmentAccessMessage(`Your assigned branch/department in the library does not match the selected issuer's default branch.`);
                        }
                    } catch (e) {
                        // if verification call fails, allow viewing but clear messages
                        setIsUserAccessBlocked(false);
                        setDepartmentAccessVerified(true);
                        setDepartmentAccessMessage('');
                    }
                    // ---- end department verification ----

                } catch (e) {
                    console.warn('Failed to compute membership status or stream/group names', e);
                }
            }
            // hide loader after verification and processing
            setIsIssuerLoading(false)

        } else {
            // keep accession-search rows on failure so faculty doesn't lose scanned resources
            const retainedAccessionRows = getAccessionSearchRows(issuedList);
            const retainedAccessionRowKeys = new Set(retainedAccessionRows.map(getResourceRowKey).filter(Boolean));
            setIssuedList(retainedAccessionRows);
            setSelectedIssuedIds(prev => prev.filter(id => retainedAccessionRowKeys.has(String(id))));
            setIssuerStudentIID('');
            setIsIssuerLoading(false)
        }

    }

    const updateResourceCondition = (id, value) => {
        setIssuedList(prev => prev.map(item => {
            if (item.id !== id) return item;

            const lateFine = Number(item.lateFine) || 0;
            const damagedPrice = Number(item.damagedPrice) || 0;
            const lostPrice = Number(item.lostPrice) || 0;

            let damagedValue = 0;
            if (value === 'Good') {
                damagedValue = 0;
            } else if (value === 'Partially Damaged') {
                damagedValue = damagedPrice;
            } else if (value === 'Resource Lost / Missing') {
                damagedValue = lostPrice;
            }

            const totalFine = lateFine + damagedValue;

            return {
                ...item,
                resourceCondition: value,
                damagedLostFine: damagedValue,
                totalFine,
            };
        }));
    };

    // Utility: show warning via shared SuccessModal component
    const showUserWarning = (title, message) => {
        setSuccessMessage(message ? `${title}: ${message}` : title || message || '');
        setSuccessModalType('warning');
        setSuccessModal(true);
    };

    const closeSuccessModal = () => {
        setSuccessModal(false);
        setSuccessModalType('');
        setSuccessMessage('');
    };

    const getPolicyRoleById = (policyId) => {
        const pid = String(policyId || '').trim();
        if (!pid) return '';

        const allRoles = Object.entries(ruleList || {});
        for (const [roleKey, policies] of allRoles) {
            if (policies && Object.prototype.hasOwnProperty.call(policies, pid)) {
                return String(roleKey || '').toLowerCase();
            }
        }

        return '';
    };

    const parsePolicyLimit = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const getPolicyLimitValidationResult = useCallback((rows = issuedList, selectedIds = selectedIssuedIds) => {
        const list = Array.isArray(rows) ? rows : [];
        const selectedSet = new Set((Array.isArray(selectedIds) ? selectedIds : []).map(id => String(id)));
        const baseIssuedCount = {};
        const selectedIssueCount = {};
        const selectedRowsToIssue = [];

        const makeKey = (roleKey, policyId) => `${String(roleKey || '').toLowerCase().trim()}::${String(policyId || '').trim()}`;

        list.forEach((row) => {
            const policyId = String(row?.policy_id || row?.selectedPolicyId || row?.policyId || '').trim();
            const rowRole = String(row?.selectedUserRole || row?.issuerRole || currentUserRole || '').toLowerCase().trim();
            const status = String(row?.bookStatus || '').toLowerCase();

            if (!policyId || !rowRole) return;

            const key = makeKey(rowRole, policyId);

            if (status !== 'available') {
                baseIssuedCount[key] = (baseIssuedCount[key] || 0) + 1;
                return;
            }

            if (selectedSet.has(String(row?.id))) {
                selectedIssueCount[key] = (selectedIssueCount[key] || 0) + 1;
                selectedRowsToIssue.push(row);
            }
        });

        const errorByRowId = {};

        selectedRowsToIssue.forEach((row) => {
            const policyId = String(row?.policy_id || row?.selectedPolicyId || row?.policyId || '').trim();
            const rowRole = String(row?.selectedUserRole || row?.issuerRole || currentUserRole || '').toLowerCase().trim();
            if (!policyId || !rowRole) return;

            const rowPolicyIid = normalizeIidValue(
                row?.userInstituteId
                || row?.resourceInstituteId
                || issuerStudentIID
                || ((selectedInstitute === 'all') ? config.institute_id : selectedInstitute)
                || config.institute_id
            );
            const rolePolicies = getRolePoliciesByIid(rowRole, rowPolicyIid);
            const policyConfig = rolePolicies ? rolePolicies[policyId] : null;
            if (!policyConfig) return;

            const maxAllowed = parsePolicyLimit(policyConfig.NumberOfResources);
            const key = makeKey(rowRole, policyId);
            const projectedCount = (baseIssuedCount[key] || 0) + (selectedIssueCount[key] || 0);

            if (projectedCount > maxAllowed) {
                errorByRowId[String(row.id)] = 'Issue limit exceeded.';
            }
        });

        return {
            hasExceeded: Object.keys(errorByRowId).length > 0,
            errorByRowId,
        };
    }, [currentUserRole, getRolePoliciesByIid, issuedList, issuerStudentIID, selectedInstitute, selectedIssuedIds]);

    const validatePolicyIssueLimits = useCallback((rows = issuedList, selectedIds = selectedIssuedIds, { syncState = false } = {}) => {
        const result = getPolicyLimitValidationResult(rows, selectedIds);
        if (syncState) {
            setPolicyLimitErrors(result.errorByRowId);
        }
        return result;
    }, [getPolicyLimitValidationResult, issuedList, selectedIssuedIds]);

    useEffect(() => {
        const result = getPolicyLimitValidationResult(issuedList, selectedIssuedIds);
        setPolicyLimitErrors(result.errorByRowId);
    }, [getPolicyLimitValidationResult, issuedList, selectedIssuedIds]);

    // Validate issued resource submission: check reservation queue and show confirmation if needed
    const validateIssuedResourceSubmission = async () => {
        if (isUserAccessBlocked) {
            showUserWarning('Warning', departmentAccessMessage || 'This user is blocked and cannot be issued new resources.');
            return;
        }

        if (hasNoDueCertificate) {
            showUserWarning('Warning', 'Cannot issue resources while No Due Certificate is active for this user.');
            return;
        }

        if (!selectedIssuedIds || selectedIssuedIds.length === 0) {
            showUserWarning('Warning', 'Please select at least one resource to continue.');
            return;
        }

        // Ensure selected rows exist and are available for issuing
        const rows = issuedList.filter(r => selectedIssuedIds.includes(r.id));
        if (!rows.length) {
            showUserWarning('Warning', 'Selected resources not found.');
            return;
        }

        const policyLimitCheck = validatePolicyIssueLimits(issuedList, selectedIssuedIds, { syncState: true });
        if (policyLimitCheck.hasExceeded) {
            showUserWarning('Warning', 'Issue limit exceeded. Please change policy for selected resource(s).');
            return;
        }

        // Check if any selected resource is already issued to a different user/department
        const currentUserId = String(issuerUniqueId || userDetails.erpId || '').trim();
        const issuedToOtherRow = rows.find(r =>
            r.isBookIssued === true &&
            currentUserId &&
            String(r.userUniqueId || '').trim() &&
            String(r.userUniqueId).trim() !== currentUserId
        );
        if (issuedToOtherRow) {
            // const issuedBy = issuedToOtherRow.issuerName || issuedToOtherRow.userUniqueId || 'another user/department';
            showUserWarning('Warning', `You cannot issue this resource as it is already issued`);
            return;
        }

        // All selected resources must be AVAILABLE to be issued
        const hasNonAvailable = rows.some(r => String(r.bookStatus || '').toLowerCase() !== 'available');
        if (hasNonAvailable) {
            showUserWarning('Warning', 'Selected resources include items that are not available to issue. Please select available resources only.');
            return;
        }

        // While issuing available resources from resource search, ensure user ERP is provided
        const hasAvailable = rows.some(r => String(r.bookStatus || '').toLowerCase() === 'available');
        const effectiveUserErp = String(userDetails.erpId || '').trim();
        if (searchRole !== 'department' && hasAvailable && !effectiveUserErp) {
            showUserWarning('Warning', 'Enter User ERP to Issue resource');
            return;
        }

        // Guard: selected user role and resource policy role must match before issuing
        const effectiveIssueRole = String(currentUserRole || '').toLowerCase().trim();
        if (searchRole !== 'department' && effectiveIssueRole) {
            const hasRolePolicyMismatch = rows.some((row) => {
                const policyId = String(row.policy_id || row.selectedPolicyId || row.policyId || '').trim();
                if (!policyId) return false;

                const rowPolicyIid = normalizeIidValue(
                    row?.userInstituteId
                    || row?.resourceInstituteId
                    || issuerStudentIID
                    || ((selectedInstitute === 'all') ? config.institute_id : selectedInstitute)
                    || config.institute_id
                );
                const rolePolicies = getRolePoliciesByIid(effectiveIssueRole, rowPolicyIid);

                const isAllowedForRole = Object.prototype.hasOwnProperty.call(rolePolicies, policyId);
                if (isAllowedForRole) return false;

                const policyRole = getPolicyRoleById(policyId);
                const rowRole = String(row.selectedUserRole || row.issuerRole || '').toLowerCase().trim();

                return (policyRole && policyRole !== effectiveIssueRole)
                    || (rowRole && rowRole !== effectiveIssueRole)
                    || true;
            });

            if (hasRolePolicyMismatch) {
                showUserWarning('Warning', 'The resource cannot be issued because the role and policy do not match.');
                return;
            }
        }

        // For now, check reservation for the first selected resource (matches example behavior)
        const firstId = selectedIssuedIds[0];
        const resourceDetails = issuedList.find(r => r.id === firstId) || null;

        setIsIssuerLoading(true);
        try {
            // derive key used to cache reservation responses
            const key = resourceDetails ? (resourceDetails.resourceCopyId ? String(resourceDetails.resourceCopyId) : (resourceDetails.id ? String(resourceDetails.id) : '')) : '';

            // try to reuse cached reservation response first
            let reservationResp = key ? (reservationCache[key] || null) : null;

            // fallback to any attached reservationQueue on the resource object
            if (!reservationResp && resourceDetails?.reservationQueue) {
                reservationResp = resourceDetails.reservationQueue;
            }

            // If we have no reservation info, assume no reservations (do not call API here)
            const reservationData = reservationResp?.data || {};
            const reservationCount = reservationResp ? Object.keys(reservationData).length : 0;

            if (!reservationResp || reservationCount === 0) {
                setIsIssuerLoading(false);
                proceedIssuedResource();
                return;
            }

            // Build dynamic message and show confirmation modal
            const firstKey = Object.keys(reservationData)[0];
            const reservationUser = reservationData[firstKey] || {};
            const erpId = reservationResp.firstUserErpId || reservationUser.erpId || reservationUser.erp_id || reservationUser.erp || '';

            const message = `This book is already reserved by (ERP ID: ${erpId}). The total queue for this book is ${reservationCount}.`;

            setIssueConfirmationMessage(message);
            setIssueConfirmationQuestion('Do you want to continue issuing the book?');
            setIssueConfirmationResource(resourceDetails);
            setShowIssueConfirm(true);
        } catch (err) {
            console.error('Error validating reservation before issue:', err);
            // On error, allow proceed as a fallback
            proceedIssuedResource();
        } finally {
            setIsIssuerLoading(false);
        }
    };

    const proceedIssuedResource = () => {
        // Build and call the IssueLibraryResource API using selected rows
        (async () => {
            if (!selectedIssuedIds || selectedIssuedIds.length === 0) {
                showUserWarning('Warning', 'Please select at least one resource to continue.');
                setShowIssueConfirm(false);
                return;
            }

            const policyLimitCheck = validatePolicyIssueLimits(issuedList, selectedIssuedIds, { syncState: true });
            if (policyLimitCheck.hasExceeded) {
                showUserWarning('Warning', 'Issue limit exceeded. Please change policy for selected resource(s).');
                setShowIssueConfirm(false);
                return;
            }

            setIsIssuerLoading(true);
            setShowIssueConfirm(false);

            try {
                // determine institute values
                const selectedInstituteValue = (selectedInstitute === 'all')
                    ? config.institute_id
                    : selectedInstitute || config.institute_id;

                const resourIdPolicyIdArray = selectedIssuedIds.map(id => {
                    const row = issuedList.find(r => r.id === id) || {};

                    // prefer an explicitly-calculated due date (from policy) when present,
                    // otherwise use API-provided dueDate/due_date; leave empty if none
                    let dueRaw = (row.calculatedDueDate !== undefined && row.calculatedDueDate !== null && row.calculatedDueDate !== '')
                        ? row.calculatedDueDate
                        : (row.dueDate || row.due_date || '');

                    // normalize to YYYY-MM-DD when possible
                    let due = '';
                    const parsed = parseDateString(dueRaw);
                    if (parsed) {
                        const y = parsed.getFullYear();
                        const m = String(parsed.getMonth() + 1).padStart(2, '0');
                        const d = String(parsed.getDate()).padStart(2, '0');
                        due = `${y}-${m}-${d}`;
                    } else if (dueRaw && String(dueRaw).trim() !== '-') {
                        // if dueRaw is a non-dash string that didn't parse, pass it through
                        due = String(dueRaw).trim();
                    }

                    return {
                        resourceCopyId: row.resourceCopyId || row.resource_copy_id || row.id || '',
                        policyId: (row.policy_id || row.selectedPolicyId || row.policyId || '') + '',
                        dueDate: due || '',
                        issueType: row.type || row.resourcePeriodicalType || 'resource',
                        iid: row.resourceInstituteId || row.iid || selectedInstituteValue,
                    };
                }).filter(Boolean);

                if (resourIdPolicyIdArray.length < 1) {
                    showUserWarning('Warning', 'No resource to issue.');
                    setIsIssuerLoading(false);
                    return;
                }

                const payload = {
                    iid: selectedInstituteValue,
                    iidArray: linkedInstitutesArray || [],
                    issuedFromIID: selectedInstituteValue,
                    issued_by: config.faculty_id || '',
                    resourIdPolicyIdArray,
                    // When searching by department, use selectedDepartment as the user id and role 'department'
                    ...(searchRole === 'department' ? { user_id: selectedDepartment || '', user_role: 'department' } : { user_id: issuerUniqueId || userDetails.erpId || userId || '', user_role: currentUserRole || '' }),
                    academicYear: config?.instructorInfo?.AcaYearId || userDetails.academicYear || '',
                };

                const jsonResponse = await LibraryApiCall.makeApiCall(
                    'IssueLibraryResource',
                    payload,
                    '/api/library/instructor.php'
                );

                if (jsonResponse?.expired) {
                    // retry shortly (keep loader on)
                    setTimeout(proceedIssuedResource, 1000);
                    return;
                }

                const apiResult = jsonResponse.APIResult || jsonResponse;

                // Some endpoints return nested objects/strings; try to get message
                let message = (apiResult && (apiResult.message || apiResult.Message)) || jsonResponse.message || '';

                // Determine success carefully: some APIs send success=true but status="false"
                const successFlag = (jsonResponse.success === true) || (String(jsonResponse.success).toLowerCase() === 'true')
                    || (apiResult && (apiResult.success === true || String(apiResult.success).toLowerCase() === 'true'));

                // Interpret explicit status field when present. Accept 'true' or 'success' as OK.
                const statusRaw = (apiResult && (apiResult.status !== undefined ? apiResult.status : null));
                const statusStr = statusRaw === null ? null : String(statusRaw).toLowerCase();
                const apiStatusOk = (statusStr === null) || statusStr === 'true' || statusStr === 'success';

                const ok = successFlag && apiStatusOk;

                if (ok) {
                    // show success modal
                    setSuccessMessage(message || 'Resources issued successfully.');
                    setSuccessModalType('success');
                    setSuccessModal(true);

                    // refresh issuer details so UI updates
                    try {
                        const iidForRefresh = selectedInstituteValue || config.institute_id;
                        await GetLibraryResourceIssuerDetails(userDetails.erpId || userId || issuerUniqueId || '', currentUserRole, userDetails.erpId || '', iidForRefresh);
                        setSelectedIssuedIds([])
                    } catch (e) {
                        // ignore refresh errors but keep success visible
                        console.warn('Failed to refresh issuer details after issue', e);
                    }
                } else {
                    // show warning/error
                    const errMsg = (message && String(message)) || 'Something went wrong while issuing resources. Please try again.';
                    setSuccessMessage(errMsg);
                    setSuccessModalType('warning');
                    setSuccessModal(true);
                }
            } catch (err) {
                console.error('Error issuing resources:', err);
                setSuccessMessage('Something went wrong while issuing resources. Please try again later.');
                setSuccessModalType('warning');
                setSuccessModal(true);
            } finally {
                setIsIssuerLoading(false);
            }
        })();
    };

    // Handle Return or Renew action for selected resources
    const handleReturnRenew = async (actionType) => {
        if (!selectedIssuedIds || selectedIssuedIds.length === 0) {
            showUserWarning('Warning', 'Please select at least one resource to continue.');
            return;
        }

        // collect selected rows
        const rows = issuedList.filter(r => selectedIssuedIds.includes(r.id));
        if (!rows.length) {
            showUserWarning('Warning', 'Selected resources not found.');
            return;
        }

        // Prevent returning/renewing resources that are AVAILABLE
        const hasAvailable = rows.some(r => String(r.bookStatus || '').toLowerCase() === 'available');
        if (hasAvailable) {
            showUserWarning('Warning', 'Selected resources include available items which cannot be returned or renewed. Please select issued resources only.');
            return;
        }

        // Check if any selected resource is issued to a different user/department
        const currentUserIdForReturnRenew = String(issuerUniqueId || userDetails.erpId || '').trim();
        const issuedToOtherRowRR = rows.find(r =>
            currentUserIdForReturnRenew &&
            String(r.userUniqueId || '').trim() &&
            String(r.userUniqueId).trim() !== currentUserIdForReturnRenew
        );
        if (issuedToOtherRowRR) {
            const actionLabel = actionType === 'return' ? 'return' : 'renew';
            // const issuedBy = issuedToOtherRowRR.issuerName || issuedToOtherRowRR.userUniqueId || 'another user/department';
            showUserWarning('Warning', `You cannot ${actionLabel} this resource as it is already issued.`);
            return;
        }

        setIsIssuerLoading(true);

        // derive institute id for payload
        const iidValueForSelectedRow = (selectedInstitute === 'all' ? config.institute_id : selectedInstitute) || config.institute_id;
        const facultyId = config.faculty_id || '';
        const issuerDetailsFallback = issuerAndResourceDetails?.issuerDetails || {};
        const resourceDetailsFallback = issuerAndResourceDetails?.ResourceDetails || {};
        const fallbackIssuerUserId = issuerUniqueId
            || issuerAndResourceDetails?.issuerId
            || issuerDetailsFallback.userid
            || issuerDetailsFallback.userId
            || issuerDetailsFallback.UserId
            || resourceDetailsFallback.userid
            || resourceDetailsFallback.user_id
            || '';
        const fallbackIssuerRole = currentUserRole
            || issuerAndResourceDetails?.issuerRole
            || issuerDetailsFallback.role
            || resourceDetailsFallback.role
            || '';

        // derive resource department ids (unique) from selected rows
        const deptIds = Array.from(new Set(rows.map(r => r.resourceDepartmentId).filter(Boolean)));

        try {
            // verify access for these departments first
            const verifyInput = { iid: iidValueForSelectedRow, facultyID: facultyId, issuerResourcesDepartmentID: deptIds };

            const verifyResp = await LibraryApiCall.makeApiCall(
                'library_GetDepartmentAccessVerification',
                verifyInput,
                "/api/library/libraryDepartmentWiseResponsible.php"
            );

            if (verifyResp?.expired) {
                // try again shortly
                setTimeout(() => handleReturnRenew(actionType), 1000);
                setIsIssuerLoading(false);
                return;
            }

            let verifyResult = verifyResp.APIResult || verifyResp;
            if (typeof verifyResult === 'string') {
                try { verifyResult = JSON.parse(verifyResult); } catch (e) { /* ignore */ }
            }

            const allowed = verifyResult && verifyResult.errorCode !== '403' && !!verifyResult.departmentAccessVerified;
            if (!allowed) {
                showUserWarning('Warning', `Your assigned branch/department in the library does not match the selected resource's branch/department.`);
                setIsIssuerLoading(false);
                return;
            }

            // proceed to call ReturnRenewLibraryResource for each selected resource
            const results = [];
            for (const row of rows) {
                const payload = {
                    iid: iidValueForSelectedRow,
                    iidArray: selectedInstitute === 'all' ? linkedInstitutesArray : [selectedInstitute],
                    // prefer the issued-unique id provided by the API; do NOT fall back to row.id (which may be resourceCopyId)
                    issuedUniqueId: row.issuedUniqueId || issuerAndResourceDetails?.issuedUniqueId || '',
                    userId: row.userUniqueId || fallbackIssuerUserId,
                    role: row.issuerRole || fallbackIssuerRole,
                    facultyId: facultyId,
                    // resourceCopyId should come from the resource copy id field
                    resourceCopyId: row.resourceCopyId || issuerAndResourceDetails?.resourceCopyId || resourceDetailsFallback.resourceCopyId || "",
                    TotalFine: (row.totalFine !== '' && row.totalFine != null) ? Number(row.totalFine).toFixed(2) : '0.00',
                    policy_id: row.policy_id || row.policyId || resourceDetailsFallback.policy_id || 0,
                    // format due date as YYYY-MM-DD for payload
                    due_date: (() => {
                        const dObj = parseDateString(row.dueDate || row.due_date || issuerAndResourceDetails?.due_date || resourceDetailsFallback.due_date || '');
                        if (dObj) {
                            const y = dObj.getFullYear();
                            const m = String(dObj.getMonth() + 1).padStart(2, '0');
                            const dd = String(dObj.getDate()).padStart(2, '0');
                            return `${y}-${m}-${dd}`;
                        }
                        return row.dueDate || row.due_date || issuerAndResourceDetails?.due_date || resourceDetailsFallback.due_date || '';
                    })(),
                    // accept various transaction id keys or fallback to receipt number
                    transaction_id: row.receiptNumber || "",
                    type: actionType === 'renew' ? 'renew' : 'return',
                    // collected fine: prefer edited UI value, then API value
                    CollectedFine: (() => {
                        if (row.collectedFine !== '' && row.collectedFine != null) return Number(row.collectedFine).toFixed(2);
                        if (row.collected_fine != null && row.collected_fine !== '') return Number(row.collected_fine).toFixed(2);
                        return '0.00';
                    })(),
                    academicYear: row.academicYear || config?.instructorInfo?.AcaYearId,
                    lateFine: (row.lateFine !== '' && row.lateFine != null) ? Number(row.lateFine).toFixed(2) : '0.00',
                    damagedLostFine: (row.damagedLostFine !== '' && row.damagedLostFine != null) ? Number(row.damagedLostFine).toFixed(2) : (row.damaged_lost_fine != null ? Number(row.damaged_lost_fine).toFixed(2) : '0.00'),
                    totalFine: (row.totalFine !== '' && row.totalFine != null) ? Number(row.totalFine).toFixed(2) : '0.00',
                    resource_condition: row.resourceCondition || 'Good',
                    issueType: 'resource'
                };

                try {
                    const resp = await LibraryApiCall.makeApiCall('ReturnRenewLibraryResource', payload, '/api/library/instructor.php');
                    results.push({ rowId: row.id, resp });
                } catch (e) {
                    results.push({ rowId: row.id, error: e });
                }
            }

            // basic result handling: extract API messages and show via SuccessModal
            const failures = results.filter(r => r.error || !(r.resp && r.resp.success));

            const extractMessage = (resp) => {
                if (!resp) return '';
                let body = resp.APIResult || resp;
                if (typeof body === 'string') {
                    try { body = JSON.parse(body); } catch (e) { /* ignore */ }
                }
                return (body && (body.message || body.Message || body.status || body.msg)) || resp.message || '';
            };

            if (failures.length) {
                const msgs = failures.map(f => extractMessage(f.resp) || f.error?.message || 'Operation failed').filter(Boolean);
                const text = msgs.length ? msgs.join(' | ') : 'Some operations failed. Please check the details or try again.';
                setSuccessMessage(text);
                setSuccessModalType('warning');
                setSuccessModal(true);
            } else {
                const msgs = results.map(r => extractMessage(r.resp)).filter(Boolean);
                const uniqueMsgs = Array.from(new Set(msgs));
                const successText = uniqueMsgs.length ? uniqueMsgs.join(' | ') : `Successfully completed ${results.length} ${results.length === 1 ? 'operation' : 'operations'}.`;

                setSuccessMessage(successText);
                setSuccessModalType('success');
                setSuccessModal(true);

                // refresh issuer details to show updated state
                await GetLibraryResourceIssuerDetails(userDetails.erpId || userId, currentUserRole, userDetails.erpId || '', iidValueForSelectedRow);
                setSelectedIssuedIds([]);
            }

            // clear loading state
            setIsIssuerLoading(false);
        } catch (e) {
            setSuccessMessage('Something went wrong while processing return/renew. Please try again later.');
            setSuccessModalType('warning');
            setSuccessModal(true);
            setIsIssuerLoading(false);
        }
    };

    // Add No Due certificate API call
    const addNoDueCertificate = async () => {
        if (isUserAccessBlocked) {
            showUserWarning('Warning', departmentAccessMessage || 'No Due Certificate cannot be issued because access is blocked for this user.');
            return;
        }

        // close modal immediately
        setShowNoDueConfirm(false);

        const noDueInstituteId = String(issuerStudentIID || '').trim()
            || ((selectedInstitute === 'all'
                ? config.institute_id
                : selectedInstitute) || config.institute_id);

        let requestData = {
            'iid': noDueInstituteId,
            'userId': issuerUniqueId,
            'userRole': currentUserRole,
            'issuedBy': config.faculty_id,
        };

        let jsonResponse = await LibraryApiCall.makeApiCall(
            'library_AddLibraryNoDueCertificate',
            requestData,
            '/api/library/libraryNoDueCertificate.php'
        );

        if (jsonResponse.expired) {
            setTimeout(() => {
                this.addNoDueCertificate();
            }, 1000);
        } else {
            if (jsonResponse.success) {
                let apiResult = jsonResponse.APIResult;
                if (apiResult.status === "success") {
                    toast.success(apiResult.message, {
                        position: "top-right",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                } else {
                    toast.warn(apiResult.message, {
                        position: "top-right",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                }
            } else {
                toast.error('Something went wrong while updating no due certificate. Please try again later.', {
                    position: "top-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
        }
    };

    const updateIssuedField = (id, field, value) => {
        setIssuedList(prev => prev.map(item => {
            if (item.id !== id) return item;
            const updated = { ...item };
            if (field === 'damagedLostFine' || field === 'collectedFine') {
                // Store the raw string as-is so the input is not disrupted while typing
                // (e.g. "12." or "12.5" must not be reformatted back to "12.00" mid-edit)
                updated[field] = value;

                // if damagedLostFine changed, recalc totalFine = lateFine + damagedLostFine
                if (field === 'damagedLostFine') {
                    const late = Number(updated.lateFine) || 0;
                    const dmg = value === '' ? 0 : (isNaN(Number(value)) ? 0 : Number(value));
                    updated.totalFine = late + dmg;
                }
            } else {
                updated[field] = value;
            }
            return updated;
        }));
    };

    // Validate damaged/lost fine input string similar to iframe logic
    const validateDamagedFineString = (value) => {
        if (value === '') return '';

        // Remove invalid characters (no minus sign)
        value = value.replace(/[^0-9.]/g, '');

        // Allow only one decimal point
        const dotCount = (value.match(/\./g) || []).length;
        if (dotCount > 1) {
            value = value.substring(0, value.lastIndexOf('.'));
        }

        // If starts with dot, prefix 0
        if (value.startsWith('.')) {
            value = '0' + value;
        }

        // Limit to 2 decimal places (ONLY if decimals exist)
        if (value.includes('.')) {
            let [intPart, decPart] = value.split('.');
            decPart = decPart.substring(0, 2);
            value = intPart + '.' + decPart;
        }

        return value;
    };

    const handleDamagedInputChange = (id, rawValue) => {
        const v = validateDamagedFineString(rawValue);
        // Pass the validated string directly; updateIssuedField stores it as-is
        updateIssuedField(id, 'damagedLostFine', v);
    };

    const handleCollectedInputChange = (id, rawValue) => {
        const v = validateDamagedFineString(rawValue);

        // find the row to get applicable totalFine
        const row = issuedList.find(it => it.id === id);
        const applicableFine = row ? Number(row.totalFine) || 0 : 0;
        const numeric = v === '' ? 0 : Number(v);

        if (v !== '' && !isNaN(numeric) && numeric > applicableFine) {
            setCollectedFineErrors(prev => ({ ...prev, [id]: 'Collected fine can not be more than applicable fine.' }));
        } else {
            setCollectedFineErrors(prev => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
        }

        // Pass the validated string directly; updateIssuedField stores it as-is
        updateIssuedField(id, 'collectedFine', v);
    };

    const handleReceiptChange = (id, rawValue) => {
        const val = rawValue ? String(rawValue).substring(0, 250) : '';
        updateIssuedField(id, 'receiptNumber', val);
    };

    // Remove an issued-list entry (used when barcode lookup returns an already-issued resource)
    const removeIssuedEntry = (id) => {
        setIssuedList(prev => Array.isArray(prev) ? prev.filter(r => String(r.id) !== String(id) && String(r.resourceCopyId || '') !== String(id)) : prev);
        setSelectedIssuedIds(prev => Array.isArray(prev) ? prev.filter(x => String(x) !== String(id)) : prev);
    };

    // Parse a date string in several common formats and return a Date or null
    const parseDateString = (s) => {
        if (!s || typeof s !== 'string') return null;
        // Try native parse first
        const d1 = new Date(s);
        if (!isNaN(d1.getTime())) return d1;

        // Try dd-mm-yyyy or dd/mm/yyyy
        const m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:\s.*)?$/);
        if (m) {
            const day = parseInt(m[1], 10);
            const month = parseInt(m[2], 10) - 1;
            const year = parseInt(m[3], 10);
            const d2 = new Date(year, month, day);
            if (!isNaN(d2.getTime())) return d2;
        }

        // Try yyyy-mm-dd (without timezone)
        const m2 = s.match(/^(\d{4})[-](\d{1,2})[-](\d{1,2})/);
        if (m2) {
            const year = parseInt(m2[1], 10);
            const month = parseInt(m2[2], 10) - 1;
            const day = parseInt(m2[3], 10);
            const d3 = new Date(year, month, day);
            if (!isNaN(d3.getTime())) return d3;
        }

        return null;
    };

    // Determine whether a row is overdue by comparing dueDate to today
    const isRowOverdue = (row) => {
        const due = parseDateString(row.dueDate || row.due_date || '');
        if (!due) return false;
        // Compare only date portion (ignore time)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        due.setHours(0, 0, 0, 0);
        return due < today;
    };

    const formatIssueDateForDisplay = (value) => {
        const parsedDate = parseDateString(String(value || '').trim());
        if (!parsedDate) return value || '-';
        return parsedDate.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getPolicyLabelById = (policyId, roleHint = '', iidHint = '') => {
        const pid = String(policyId || '').trim();
        if (!pid) return '';

        const roleKey = String(roleHint || '').toLowerCase();

        if (roleKey) {
            const rolePolicies = getRolePoliciesByIid(roleKey, iidHint);
            if (rolePolicies && rolePolicies[pid]) {
                const p = rolePolicies[pid] || {};
                return p.name || p.policy_name || p.policyName || p.stream_name || pid;
            }
        }

        if (roleKey && ruleList && ruleList[roleKey] && ruleList[roleKey][pid]) {
            const p = ruleList[roleKey][pid] || {};
            return p.name || p.policy_name || p.policyName || p.stream_name || pid;
        }

        const allPolicyBuckets = Object.values(ruleList || {});
        for (const bucket of allPolicyBuckets) {
            if (bucket && bucket[pid]) {
                const p = bucket[pid] || {};
                return p.name || p.policy_name || p.policyName || p.stream_name || pid;
            }
        }

        return pid;
    };

    // Handle department selection: call issuer details for department role
    const handleDepartmentSelect = async (department) => {
        // Ensure local state reflects the newly-selected department and role
        setSelectedDepartment(department);
        setCurrentUserRole('department');

        // Clear any previous department verification so the table/loader is shown
        // while we fetch fresh data for the newly-selected department.
        setDepartmentAccessVerified(null);
        setDepartmentAccessMessage('');

        // Ensure sections are visible immediately so loader displays
        setShowSections(true);

        const iidValue = selectedInstitute === 'all' ? config.institute_id : selectedInstitute;
        await GetLibraryResourceIssuerDetails(department, 'department', 0, iidValue);
    };

    const clearAccessionSearchState = () => {
        clearAccessionSuggestionsDebounce();
        setLoadingAccessionData(false);
        const retainedIssuerRows = Array.isArray(issuedList)
            ? issuedList.filter(row => row?.resourceRowSource !== 'accession')
            : [];
        const retainedIssuerRowKeys = new Set(retainedIssuerRows.map(getResourceRowKey).filter(Boolean));
        const hasResolvedUserContext = !!String(issuerUniqueId || userDetails.erpId || '').trim() || !!String(userDetails.name || '').trim();

        setAccession(null);
        setSearchByAccession('');
        setAccessionOptions([]);
        setAccessionNumber('');
        setSelectedAccessionMeta({ barcode: '', accessionSeriesId: '' });
        setResourceAvailabilityMessage('');
        setIssuedList(retainedIssuerRows);
        setSelectedIssuedIds(prev => prev.filter(id => retainedIssuerRowKeys.has(String(id))));
        setIssuerAndResourceDetails(null);
        setIsResourceSearchActive(false);
        setResourceSearchHasIssuerDetails(false);
        setPolicyDueDates({});
        setReservationCache({});
        setShowSections(retainedIssuerRows.length > 0 || hasResolvedUserContext);
    };


    // Clear user search state but keep accession search results (used when user search is performed after accession search)
    const clearUserSearchState = () => {
        clearUserSuggestionsDebounce();
        setSearchByUserDetails('');
        setUserOptions([]);
        setShowUserDetailsDropdown(false);
        setLoadingUserData(false);
        setUserId('');
        setResourceAvailabilityMessage('');
        setSelectedIssuedIds([]);
        setIssuedList([]);
        resetUserState();
        setShowSections(false);
        setHasUserSearched(false);
    };

    const handleSearchAccession = async (val, accessionMetaParam = null) => {
        const q = getAccessionQueryValue(val || accessionNumber || '');
        setAccessionNumber(q);

        const matchedOption = (Array.isArray(accessionOptions) ? accessionOptions : []).find((opt) => {
            const queryFromOption = getAccessionQueryValue(opt?.value || '');
            return String(queryFromOption || '').trim() === String(q || '').trim();
        });

        const resolvedAccessionMeta = accessionMetaParam || {
            barcode: String(
                selectedAccessionMeta?.barcode
                || matchedOption?.barcode
                || matchedOption?.raw?.accessionNumber
                || matchedOption?.raw?.accessionnumber
                || matchedOption?.raw?.accession_number
                || ''
            ),
            accessionSeriesId: String(
                selectedAccessionMeta?.accessionSeriesId
                || matchedOption?.accessionSeriesId
                || matchedOption?.raw?.accession_series_id
                || matchedOption?.raw?.accessionSeriesId
                || matchedOption?.raw?.accessionSeries_id
                || ''
            ),
        };

        if (!q) {
            const retainedIssuerRows = Array.isArray(issuedList)
                ? issuedList.filter(row => row?.resourceRowSource !== 'accession')
                : [];
            const retainedIssuerRowKeys = new Set(retainedIssuerRows.map(getResourceRowKey).filter(Boolean));
            const hasResolvedUserContext = !!String(issuerUniqueId || userDetails.erpId || '').trim() || !!String(userDetails.name || '').trim();

            setIssuedList(retainedIssuerRows);
            setSelectedIssuedIds(prev => prev.filter(id => retainedIssuerRowKeys.has(String(id))));
            setShowSections(retainedIssuerRows.length > 0 || hasResolvedUserContext);
            setResourceAvailabilityMessage('');
            setIsResourceSearchActive(false);
            setResourceSearchHasIssuerDetails(false);

            return;
        }

        setIsResourceSearchActive(true);
        setResourceSearchHasIssuerDetails(false);

        // Ensure the Issued Resources table (Section 3) is visible while searching
        // and reset department-access verification so the table is not hidden
        // incorrectly while the loader runs.
        setShowSections(true);
        setDepartmentAccessVerified(null);
        setDepartmentAccessMessage('');

        setIsIssuerLoading(true);

        const iidArray = (selectedInstitute === 'all') ? linkedInstitutesArray : [selectedInstitute];
        const iidValue = (selectedInstitute === 'all') ? config.institute_id : selectedInstitute;

        const payload = {
            iidArray: iidArray || [],
            iid: iidValue || config.institute_id,
            barcode: resolvedAccessionMeta.barcode || '',
            accessionSeriesId: resolvedAccessionMeta.accessionSeriesId || '',
            type: '',
            receiverUserId: config.faculty_id || '',
            // accessionSeries_number: q,
        };

        try {
            const jsonResponse = await LibraryApiCall.makeApiCall(
                'GetLibraryResourceDetailsByBarcode',
                payload,
                '/api/library/instructor.php'
            );

            if (jsonResponse?.expired) {
                setTimeout(() => handleSearchAccession(q, resolvedAccessionMeta), 1000);
                return;
            }

            if (jsonResponse?.success) {
                const apiResult = jsonResponse.APIResult || jsonResponse;   
                
                // If the API explicitly reports a failure (e.g. missing mandatory fields), show toast and stop
                if (apiResult.status == 'fail') {
                    const failMessage = jsonResponse.message || apiResult.message || 'Request failed. Please try again.';
                    toast.error(failMessage, { position: 'top-right', autoClose: 4000 });
                    setIsIssuerLoading(false);
                    return;
                }

                // Check resource availability based on resourceStatus/barcode/data
                const resourceStatusValue = apiResult.resourceStatus ? String(apiResult.resourceStatus).toLowerCase().trim() : '';
                const barcodeValue = apiResult.barcode ? String(apiResult.barcode).toLowerCase().trim() : '';

                const apiMessage = (apiResult.message && String(apiResult.message).trim())
                    ? String(apiResult.message).trim()
                    : 'This resource is not available for issue.';

                let issuerResourceDetails = null;

                // Priority 1: explicit resource status from API
                if (resourceStatusValue && resourceStatusValue !== 'available' && apiResult.message) {
                    setResourceAvailabilityMessage(apiMessage);
                } else if (barcodeValue && barcodeValue !== 'available') {
                    setResourceAvailabilityMessage('Resource/Periodical not found.');
                } else {
                    setResourceAvailabilityMessage('');
                    // Resource appears available — fetch issuer & resource details for department verification
                    issuerResourceDetails = await getIssuerAndResourceDetails(q);
                }

                // top-level flags that may accompany `data`
                const topLevelFlags = {
                    isbookIssued: apiResult.isbookIssued,
                    barcode: apiResult.barcode,
                    resourceStatus: apiResult.resourceStatus,
                    notAllowedStatusArray: apiResult.notAllowedStatusArray,
                };

                // prefer nested `data` when present
                const payload = (apiResult && apiResult.data) ? apiResult.data : apiResult;

                // normalize API items into the table row shape
                const normalize = (item, ctx = {}, issuerCtx = null) => {
                    const resourceDetails = issuerCtx?.ResourceDetails || {};
                    const issuerDetails = issuerCtx?.issuerDetails || {};
                    const accessionNum = item.accessionnumber || item.accession_number || item.accessionNumber || item.accessionnumber || '';
                    const accessionSeries = item.accession_series || item.accessionSeries || item.accession_series || '';
                    const accessionDetails = accessionSeries && accessionNum ? `${accessionSeries}-${accessionNum}` : (item.accessionDetails || item.accession || accessionNum || q || '-');
                    const lateFineValue = Number(item.lateFine || item.fine_amount || issuerCtx?.lateFine || resourceDetails.lateFine || 0) || 0;
                    const damagedPriceValue = Number(item.damagedPrice || item.damaged_price || issuerCtx?.damagedPrice || resourceDetails.damagedPrice || 0) || 0;
                    const lostPriceValue = Number(item.lostPrice || item.lost_price || issuerCtx?.lostPrice || resourceDetails.lostPrice || 0) || 0;
                    const collectedFineValue = Number(item.collected_fine || item.collectedFine || resourceDetails.collected_fine || issuerCtx?.collected_fine || 0) || 0;
                    const initialDamagedLostFine = Number(item.damagedLostFine || item.damaged_lost_fine || 0) || 0;
                    const totalFineValue = Number(item.totalFine ?? (lateFineValue + initialDamagedLostFine)) || 0;
                    const roleFromIssuer = (item.role || issuerCtx?.issuerRole || issuerDetails.role || resourceDetails.role || '').toString().toLowerCase();
                    const isIssuedFromIssuerApi = !!(issuerCtx && (issuerCtx.issuedUniqueId || resourceDetails.issuer === 'available' || issuerDetails.issuer === 'available'));
                    const isIssuedFromBarcodeApi = (ctx.isbookIssued === true || String(ctx.isbookIssued).toLowerCase() === 'true');
                    const isIssued = isIssuedFromIssuerApi || isIssuedFromBarcodeApi;

                    // determine policy: prefer item's policy fields, otherwise pick first policy for current role from ruleList
                    const existingPolicy = item.policy_id || item.policyId || item.policyUID || item.policy || '';
                    const roleKeyRaw = (roleFromIssuer || currentUserRole).toString().toLowerCase();
                    const issuerPolicyIid = normalizeIidValue(
                        item.userInstituteId
                        || item.iid
                        || item.instituteId
                        || item.institute_id
                        || issuerDetails.studentIID
                        || issuerDetails.studentIid
                        || issuerDetails.student_iid
                        || issuerDetails.iid
                        || issuerDetails.instituteId
                        || issuerDetails.institute_id
                        || ((selectedInstitute === 'all') ? config.institute_id : selectedInstitute)
                        || config.institute_id
                    );
                    const defaultPolicyOptions = buildPolicyOptions(roleKeyRaw, issuerPolicyIid);
                    const firstPolicy = (defaultPolicyOptions && defaultPolicyOptions.length) ? defaultPolicyOptions[0].value : '';
                    const selectedPolicyId = existingPolicy ? String(existingPolicy) : (firstPolicy || '');

                    return {
                        accessionSeries: accessionSeries || item.accessionSeries || '',
                        accessionSeriesId: item.accession_series_id || item.accessionSeriesId || item.accessionSeries_id || null,
                        accessionDetails: accessionDetails,
                        accessionNumber: accessionNum,
                        resourceCopyId: item.resourceCopyId || item.resource_copy_id || item.copyId || item.copy_id || issuerCtx?.resourceCopyId || resourceDetails.resourceCopyId || null,
                        // Prefer issuer-details API signal for return/renew flow; fallback to barcode API flag
                        bookStatus: isIssued ? "Issued" : "Available",
                        title: item.title || item.name || '-',
                        // use API issue date when available; otherwise default to current date (ISO yyyy-mm-dd)
                        issueDate: item.issuedDate || item.issueDate || issuerCtx?.issuedDate || resourceDetails.issued_date || new Date().toISOString().split('T')[0],
                        // prefer API due date when provided; otherwise leave empty so UI can request policy-calculated date
                        dueDate: item.dueDate || item.due_date || issuerCtx?.due_date || resourceDetails.due_date || '',
                        // policy fields: used to request calculated due date immediately
                        policyOptions: defaultPolicyOptions,
                        selectedPolicyId,
                        policy_id: existingPolicy ? String(existingPolicy) : (resourceDetails.policy_id ? String(resourceDetails.policy_id) : (firstPolicy || '')),
                        type: item.resourcePeriodicalType || item.type || resourceDetails.resourcePeriodicalType || '-',
                        lateFine: lateFineValue,
                        damagedPrice: damagedPriceValue,
                        lostPrice: lostPriceValue,
                        resourceCondition: roleFromIssuer === 'department' ? 'Good' : (item.resourceCondition || 'Good'),
                        // iframe behavior: starts at 0, user can update via condition/input
                        damagedLostFine: initialDamagedLostFine,
                        // iframe behavior: initial totalFine = lateFine (+ damagedLostFine if already present)
                        totalFine: totalFineValue,
                        collectedFine: collectedFineValue,
                        receiptNumber: item.receiptNumber || item.receipt_number || '-',
                        resourceInstituteName: item.resourceInstituteName || item.resourseInstituteName || issuerCtx?.resourseInstituteName || resourceDetails.resourseInstituteName || item.resourceAttachmentPath || '',
                        issuerName: item.issuerName || item.issuer_name || item.issuer || item.issuedByName || issuerDetails.name || resourceDetails.issuerName || '',
                        // ensure id exists and prefer resourceCopyId when available
                        id: item.id || item.issued_unique_id || item.uniqueId || issuerCtx?.issuedUniqueId || (item.resourceCopyId ? String(item.resourceCopyId) : (item.resource_copy_id ? String(item.resource_copy_id) : String(Math.random()))),
                        issuedUniqueId: item.issuedUniqueId || item.issued_unique_id || issuerCtx?.issuedUniqueId || '',
                        userUniqueId: item.user_id || item.userId || item.userid || issuerCtx?.issuerId || issuerDetails.userid || resourceDetails.userid || '',
                        issuerRole: item.role || issuerCtx?.issuerRole || issuerDetails.role || resourceDetails.role || '',
                        selectedUserRole: roleFromIssuer || currentUserRole || '',
                        userInstituteId: issuerPolicyIid,
                        // extra flags
                        isBookIssued: isIssued,
                        resourceRowSource: 'accession',
                        raw: item,
                    };
                };

                let availableList = [];
                if (payload && !Array.isArray(payload)) {
                    availableList = [normalize(payload, topLevelFlags, issuerResourceDetails)];
                } else if (Array.isArray(payload)) {
                    availableList = payload.map(it => normalize(it, topLevelFlags, issuerResourceDetails));
                }

                // Merge with already issued resources (if any): update existing entries when keys match, otherwise append
                const existing = Array.isArray(issuedList) ? issuedList.slice() : [];

                const existingIndexByKey = new Map();
                existing.forEach((e, idx) => {
                    const k = getResourceRowKey(e);
                    if (k) existingIndexByKey.set(k, idx);
                });

                for (const a of availableList) {
                    try {
                        // 1) Check reservation queue for this resource
                        const reservationResp = await checkReservationQueueForResource(a, a.raw);
                        if (reservationResp) {
                            // attach reservation response for UI or later logic
                            a.reservationQueue = reservationResp;

                            // cache it for later reuse (key by resourceCopyId or id)
                            const k = getResourceRowKey(a) || (a.resourceCopyId ? String(a.resourceCopyId) : (a.id ? String(a.id) : ''));
                            if (k) {
                                setReservationCache(prev => ({ ...prev, [k]: reservationResp }));
                            }
                        }

                        // // 2) Check department verification for this resource
                        // const deptOk = await checkDepartmentVerificationForResource(a);

                        // if (!deptOk) {
                        //     // department verification failed: set state and skip adding this resource
                        //     setDepartmentAccessVerified(false);
                        //     setDepartmentAccessMessage(`Your assigned branch/department in the library does not match the selected resource's branch/department.`);
                        //     // do not add this resource to the list
                        //     continue;
                        // }

                        // // department is allowed; ensure global flag is set
                        // setDepartmentAccessVerified(true);
                        // setDepartmentAccessMessage('');

                        // 3) If allowed, call policy due-date API when policy id is available
                        // prefer policy id from raw API payload
                        const policyId = (a.raw && (a.raw.policy_id || a.raw.policyId || a.raw.policyUID)) || a.policy_id || a.policyId || null;
                        if (policyId) {
                            // Prefer resourceCopyId as the map key, but fall back to the item's id so
                            // we still store and display a calculated due date when resourceCopyId
                            // is not available from the API response.
                            const rcid = a.resourceCopyId || a.resource_copy_id || a.id || null;
                            if (rcid) {
                                // call and attach calculated due date (function will update issuedList when it finds the resource).
                                // Assign the returned value even if it's an empty string/null so the row carries the API result
                                // and the UI can react to it immediately after setting the list.
                                const calculated = await getPolicyWiseDueDate(policyId, rcid);
                                a.calculatedDueDate = (calculated === undefined) ? null : calculated;
                            }
                        }

                        // merge into existing list (preserve any UI-entered values)
                        const k = getResourceRowKey(a);
                        if (k && existingIndexByKey.has(k)) {
                            const idx = existingIndexByKey.get(k);
                            const prev = existing[idx] || {};
                            existing[idx] = {
                                ...prev,
                                ...a,
                                collectedFine: (prev.collectedFine !== undefined && prev.collectedFine !== null && prev.collectedFine !== '') ? prev.collectedFine : a.collectedFine,
                                damagedLostFine: (prev.damagedLostFine !== undefined && prev.damagedLostFine !== null && prev.damagedLostFine !== '') ? prev.damagedLostFine : a.damagedLostFine,
                            };
                        } else {
                            existing.push(a);
                        }
                    } catch (err) {
                        console.error('Error while running reservation/department/policy checks for resource', a, err);
                        // continue processing other items
                        continue;
                    }
                }

                setIssuedList(existing);
            } else {
                // Do not clear existing issuedList when accession lookup returns no results
                // Keep current list untouched so users still see previously loaded resources
                // (this prevents accidental clearing when API returns no data)
                // setIssuedList([]);
            }
        } catch (e) {
            console.error('Error fetching resource details by barcode', e);
        } finally {
            setIsIssuerLoading(false);
        }
    };


    // Handle user search: clear accession search state and fetch issuer details for the searched user
    const handleSearchUserData = async (val) => {
        const searchUserId = getUserQueryValue(val || '');
        resetUserState({ preserveAccessionResults: true });
        setIsResourceSearchActive(false);
        setResourceSearchHasIssuerDetails(false);
        setHasUserSearched(true);
        fetchScannedEntries(searchUserId);
    };

    // Check reservation queue for a resource
    const checkReservationQueueForResource = async (resourceDetails, apiResponseForResourceDetails) => {
        if (!resourceDetails) return null;

        const inputParamObject = {
            iid: selectedInstitute === 'all' ? config.institute_id : selectedInstitute,
            iidArray: linkedInstitutesArray || [],
            resourceCopyId: resourceDetails.resourceCopyId || resourceDetails.resource_copy_id || resourceDetails.id,
            // When searching by department, pass department unique id as userId
            ...(searchRole === 'department' ? { userId: selectedDepartment || '' } : { userId: issuerUniqueId || userDetails.erpId || '' }),
            // When searching by department, explicitly tell the API the caller role is 'department'
            userRole: (searchRole === 'department') ? 'department' : currentUserRole
        };

        try {
            const resp = await LibraryApiCall.makeApiCall(
                'library_checkReservationQueueForResource',
                inputParamObject,
                '/api/instructor/library.php'
            );

            if (resp?.expired) {
                // retry once after short delay
                setTimeout(() => checkReservationQueueForResource(resourceDetails, apiResponseForResourceDetails), 1000);
                return null;
            }

            if (resp) {
                const parsed = typeof resp.APIResult === 'string' ? JSON.parse(resp.APIResult) : (resp.APIResult || resp);
                return parsed;
            }
        } catch (err) {
            console.error('Error checking reservation queue for resource:', err);
        }

        return null;
    };

    // Check department access verification for a resource
    const checkDepartmentVerificationForResource = async (resourceDetails) => {
        if (!resourceDetails) return false;

        const resourceDefaultDepartmentId = resourceDetails.resourceDepartmentId || resourceDetails.departmentId || resourceDetails.resource_department_id || [];

        const iidValueForSelectedRow = selectedInstitute === 'all' ? config.institute_id : selectedInstitute;
        const facultyId = config.faculty_id || '';

        const inputParamObject = {
            iid: iidValueForSelectedRow,
            facultyID: facultyId,
            issuerResourcesDepartmentID: Array.isArray(resourceDefaultDepartmentId) ? resourceDefaultDepartmentId : (resourceDefaultDepartmentId ? [resourceDefaultDepartmentId] : [])
        };

        try {
            const resp = await LibraryApiCall.makeApiCall(
                'library_GetDepartmentAccessVerification',
                inputParamObject,
                '/api/library/libraryDepartmentWiseResponsible.php'
            );

            if (resp?.expired) {
                setTimeout(() => checkDepartmentVerificationForResource(resourceDetails), 1000);
                return false;
            }

            if (resp) {
                const parsed = typeof resp.APIResult === 'string' ? JSON.parse(resp.APIResult) : (resp.APIResult || resp);
                const allowed = parsed && parsed.errorCode !== '403' && !!parsed.departmentAccessVerified;

                if (!allowed) {
                    setDepartmentAccessVerified(false);
                    setDepartmentAccessMessage(parsed?.message || `Your assigned branch/department in the library does not match the selected resource's branch/department.`);
                    return false;
                }

                setDepartmentAccessVerified(true);
                setDepartmentAccessMessage('');
                return true;
            }
        } catch (err) {
            console.error('Error checking department verification for resource:', err);
        }

        return false;
    };

    // Fetch issuer and resource details (GetLibraryIssuerAndResourceDetails)
    // Mirrors the iframe GetLibraryIssuerAndResourceDetailsCallback logic.
    // Returns the parsed data object or null on failure.
    const getIssuerAndResourceDetails = async (accessionSeriesNumber) => {
        const iidArray = (selectedInstitute === 'all') ? linkedInstitutesArray : [selectedInstitute];
        const iidValue = (selectedInstitute === 'all')
            ? config.institute_id
            : selectedInstitute;

        const inputParamObject = {
            iidArray: iidArray || [],
            iid: iidValue || config.institute_id,
            barcode: '',
            accessionSeriesId: '',
            accessionSeries_number: accessionSeriesNumber || '',
        };

        try {
            const resp = await LibraryApiCall.makeApiCall(
                'GetLibraryIssuerAndResourceDetails',
                inputParamObject,
                '/api/library/instructor.php'
            );

            if (resp?.expired) {
                setTimeout(() => getIssuerAndResourceDetails(accessionSeriesNumber), 1000);
                return null;
            }

            if (resp?.success) {
                const apiResult = resp.APIResult || resp;
                const parsed = typeof apiResult === 'string' ? JSON.parse(apiResult) : apiResult;

                // Reject 403 / error responses
                if (parsed?.errorCode === '403') {
                    console.warn('GetLibraryIssuerAndResourceDetails: 403 error', parsed?.message);
                    setResourceSearchHasIssuerDetails(false);
                    return null;
                }

                const detailsObj = parsed?.data || parsed;

                // Store the full response object in state (mirrors IssuerAndResourceDetailsObj)
                setIssuerAndResourceDetails(detailsObj);

                // ── Map issuerDetails → setUserDetails ───────────────────────────
                // CONDITION: Only update userDetails from accession search if user was NOT previously searched.
                // If user was searched before accession, keep the searched user's details and don't override with issued user's details.
                const issuer = detailsObj?.issuerDetails || {};
                const hasIssuerDetails = issuer && Object.keys(issuer).length > 0;
                setResourceSearchHasIssuerDetails(!!hasIssuerDetails);
                const resourceDetailsCount = Array.isArray(detailsObj?.ResourceDetails)
                    ? detailsObj.ResourceDetails.length
                    : (detailsObj?.ResourceDetails && typeof detailsObj.ResourceDetails === 'object' && Object.keys(detailsObj.ResourceDetails).length > 0)
                        ? 1
                        : 0;
                if (hasIssuerDetails && !hasUserSearched) {
                    const roleRaw = String(issuer.role || issuer.user_type || issuer.roleType || '').toLowerCase();
                    const isStudent = roleRaw === 'student';

                    if (isStudent) {
                        setUserDetails({
                            erpId: issuer.studentid || issuer.student_user_id || issuer.userId || issuer.userid || '',
                            name: issuer.name || (issuer.studentFirstName ? `${issuer.studentFirstName}${issuer.studentLastName ? ' ' + issuer.studentLastName : ''}` : ''),
                            issuedResources: resourceDetailsCount,
                            admissionNumber: issuer.admissionNumber || issuer.admission_number || issuer.PRN || issuer.universityNumber || '',
                            rollNumber: issuer.rollNumber || '',
                            email: issuer.student_email_id || issuer.student_email || issuer.mail || issuer.Email || '',
                            mobileNo: issuer.mobileNo || issuer.Mobile || '',
                            photo: issuer.profilePicture || issuer.profilePhoto || issuer.profileImage || '',
                            employeeId: '',
                            userRole: 'student',
                        });
                    } else {
                        setUserDetails({
                            erpId: issuer.vmedulife_id || issuer.UserId || issuer.userId || issuer.userid || '',
                            name: issuer.name || (issuer.employeeName ? `${issuer.employeeName}${issuer.employeeLastName ? ' ' + issuer.employeeLastName : ''}` : ''),
                            issuedResources: resourceDetailsCount,
                            admissionNumber: '',
                            email: issuer.faculty_email || issuer.Email || issuer.mail || '',
                            mobileNo: issuer.mobileNo || issuer.Mobile || '',
                            photo: issuer.profilePicture || issuer.profilePhoto || issuer.profileImage || '',
                            employeeId: issuer.employeeId || issuer.employee_id || issuer.empId || issuer.emp_id || '',
                            userRole: roleRaw || 'faculty',
                        });
                    }

                    // also set issuerUniqueId so downstream actions (Issue/Return) have correct user id
                    const candidateId = issuer.userid || issuer.userId || issuer.UserId || issuer.studentid || issuer.student_user_id || issuer.vmedulife_id || '';
                    if (candidateId) setIssuerUniqueId(String(candidateId));

                    // set role so policy/due-date/department calls use correct role
                    if (roleRaw) setCurrentUserRole(roleRaw);
                }
                // ── End issuerDetails mapping ────────────────────────────────────

                // ── Mirror iframe logic: resourcecopy guard + dept verification ──
                if (detailsObj && String(detailsObj.resourcecopy || '').toLowerCase() !== 'not available') {
                    // Derive department id from issuerDetails (mirrors iframe IssuerAndResourceDetailsDefaultDepartmentId)
                    let departmentId = [];
                    const issuerRole = String(issuer.role || '').toLowerCase();
                    if (String(detailsObj.resourcecopy || '').toLowerCase() === 'available') {
                        if (issuerRole === 'student') {
                            const sid = issuer.defaultStreamGroupData?.sid;
                            departmentId = sid ? [sid] : [];
                        } else {
                            const insti_stream_id = issuer.facultydefaultStreamGroupData?.insti_stream_id;
                            departmentId = insti_stream_id ? [insti_stream_id] : [];
                        }
                    } else {
                        // fallback to ResourceDetails for dept id
                        const rdid = detailsObj.ResourceDetails?.departmentId || detailsObj.ResourceDetails?.resourceDepartmentId || [];
                        departmentId = Array.isArray(rdid) ? rdid : (rdid ? [rdid] : []);
                    }

                    const syntheticResource = {
                        resourceDepartmentId: departmentId,
                        ...(detailsObj.ResourceDetails || {}),
                    };
                    await checkDepartmentVerificationForResource(syntheticResource);
                }
                // ── End dept verification ────────────────────────────────────────

                return detailsObj;
            }
        } catch (err) {
            console.error('Error calling GetLibraryIssuerAndResourceDetails:', err);
        }

        setResourceSearchHasIssuerDetails(false);

        return null;
    };

    // Get due date as per policy
    const getPolicyWiseDueDate = async (policyUID, resourceCopyIdToCalculateDueDate) => {
        const inputParamObject = {
            iidArray: linkedInstitutesArray || [],
            iid: selectedInstitute === 'all' ? config.institute_id : selectedInstitute,
            // if the current search partition is "department", request due-date as department
            user_role: (searchRole === 'department') ? 'department' : (currentUserRole || 'student'),
            // When searching by department, use the selected department unique id as the user id
            ...(searchRole === 'department' ? { user_id: selectedDepartment || '' } : { user_id: issuerUniqueId || userDetails.erpId || '' }),
            policy_id: policyUID
        };

        try {
            const resp = await LibraryApiCall.makeApiCall(
                'libraryGetDueDateAsPerPolicy',
                inputParamObject,
                '/api/instructor/library.php'
            );

            if (resp?.expired) {
                setTimeout(() => getPolicyWiseDueDate(policyUID, resourceCopyIdToCalculateDueDate), 1000);
                return null;
            }

            if (resp) {
                const parsed = typeof resp.APIResult === 'string' ? JSON.parse(resp.APIResult) : (resp.APIResult || resp);
                const calculatedDueDate = parsed?.dueDate || parsed?.calculatedDueDate || '';

                // update issuedList if it contains this resource
                setIssuedList(prev => prev.map(item => {
                    if (String(item.resourceCopyId) === String(resourceCopyIdToCalculateDueDate)) {
                        return { ...item, calculatedDueDate };
                    }
                    return item;
                }));

                // also keep a centralized map of calculated dates keyed by resourceCopyId
                try {
                    setPolicyDueDates(prev => ({ ...prev, [String(resourceCopyIdToCalculateDueDate)]: calculatedDueDate }));
                } catch (e) {
                    // ignore state update errors
                }

                return calculatedDueDate;
            }
        } catch (err) {
            console.error('Error getting policy wise due date:', err);
        }

        return null;
    };

    // No-Due can be issued only in explicit user-search flow
    // with a resolved user identity and role.
    const hasResolvedUserIdentity = !!String(issuerUniqueId || userDetails.erpId || '').trim();
    const hasResolvedUserRole = !!String(currentUserRole || '').trim();
    const isNoDueContextFromUserSearch = (searchRole === 'user') && !isResourceSearchActive;
    const shouldShowUserDetailsSection = searchRole === 'user'
        && (
            !isResourceSearchActive
            || resourceSearchHasIssuerDetails
            || isUserLoading
            || hasResolvedUserIdentity
            || !!String(userDetails.name || '').trim()
        );
    const disableNoDueCertificate =
        hasNoDueCertificate
        || isUserAccessBlocked
        // || !isNoDueContextFromUserSearch
        || !hasResolvedUserIdentity
        || !hasResolvedUserRole;

    return (
        // className="libraryComponents-container"
        <div>
            <div style={{ padding: 2, width: '100%' }}>
                {/* Section 1: Search partitions (card) */}
                <div style={{ marginBottom: 16 }}>
                    <div
                        style={{
                            background: '#fff',
                            border: '1px solid rgb(160, 160, 160)',
                            borderRadius: 8,
                            padding: 16,
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                        }}
                    >
                        <div style={{ display: 'flex', width: '100%', alignItems: 'stretch' }}>
                            {/* LEFT SECTION */}
                            <div style={{ flex: 1, paddingRight: 12 }}>
                                <div style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>
                                    Search By User/Department
                                </div>

                                {/* How to Use link + info icon */}
                                <div className='no-print' style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                    onMouseEnter={(e) => {
                                        const tooltip = e.currentTarget.querySelector('.custom-tooltip');
                                        if (tooltip) tooltip.style.display = 'block';
                                    }}
                                    onMouseLeave={(e) => {
                                        const tooltip = e.currentTarget.querySelector('.custom-tooltip');
                                        if (tooltip) tooltip.style.display = 'none';
                                    }}>

                                    <span
                                        aria-label="How to Use"
                                        role="button"
                                        style={{ color: '#0d6efd', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, lineHeight: 1.2 }}
                                    >
                                        How to Use
                                    </span>

                                    <span aria-label="How to Use info">
                                        <FontAwesomeIcon
                                            icon={['fas', 'info-circle']}
                                            size="sm"
                                            className="info-button"
                                            style={{ cursor: 'pointer', color: '#4a4a4a' }}
                                        />
                                    </span>

                                    <div
                                        className="custom-tooltip"
                                        style={{
                                            display: 'none',
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            background: '#fff',
                                            color: '#333',
                                            padding: '10px',
                                            width: '430px',
                                            borderRadius: '6px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                            fontSize: '13px',
                                            zIndex: 999,
                                            whiteSpace: 'normal'
                                        }}
                                    >
                                        <strong>How to Use</strong><br />
                                        <b>Method 1:</b><br />
                                        <b>1: </b>First, select Institute or All Institute<br />
                                        <b>2: </b>Then, select Role<br />
                                        <b>3: </b>Enter ERP ID or user details<br />
                                        <b>4: </b>Enter accession details<br />
                                        <b>5: </b>Finally, issue the book<br /><br />
                                        <b>Method 2:</b><br />
                                        <b>1: </b>First, enter accession details<br />
                                        <b>2: </b>Then, enter user details (or scan user details)<br />
                                        <b>3: </b>After entering user details, press Enter again on accession details so that the policy gets applied<br />
                                        <b>4: </b>Finally, issue the book
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 12, width: '100%' }}>
                                    {/* Institute */}
                                    <div style={{ flex: 1 }}>
                                        <label className="vm_custom_body5">Institute</label>
                                        <Select
                                            options={[
                                                { value: 'all', label: 'All Institute' },
                                                ...Object.values(assignedInstitutes).map(ins => ({
                                                    value: String(ins.iid),
                                                    label: ins.institute_name
                                                }))
                                            ]}
                                            value={(function () {
                                                const opts = [
                                                    { value: 'all', label: 'All Institute' },
                                                    ...Object.values(assignedInstitutes).map(ins => ({
                                                        value: String(ins.iid),
                                                        label: ins.institute_name
                                                    }))
                                                ];
                                                return opts.find(o => o.value === String(selectedInstitute)) || opts[0];
                                            })()}
                                            onChange={(opt) => {
                                                const nextInstituteValue = opt?.value || 'all';
                                                setSelectedInstitute(nextInstituteValue);
                                                setUserId('');
                                                setIsResourceSearchActive(false);
                                                setResourceSearchHasIssuerDetails(false);
                                                resetUserState();
                                                const nextInstituteArray = nextInstituteValue === 'all' ? linkedInstitutesArray : [nextInstituteValue];
                                                GetInstituteLibraryRulesHolidyCount(nextInstituteArray, nextInstituteValue);
                                                GetMultipleInstituteStreamsObject(nextInstituteValue);
                                            }}
                                            styles={{ container: base => ({ ...base, width: '100%' }), menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>

                                    {/* Role */}
                                    <div style={{ flex: 1 }}>
                                        <label className="vm_custom_body5">Role</label>
                                        <Select
                                            options={[
                                                { value: 'user', label: 'Search by user' },
                                                { value: 'department', label: 'Search by department' }
                                            ]}
                                            value={{
                                                value: searchRole,
                                                label: searchRole === 'department'
                                                    ? 'Search by department'
                                                    : 'Search by user'
                                            }}
                                            onChange={(opt) => {
                                                const val = opt?.value || 'user';
                                                setSearchRole(val);
                                                setUserId('');
                                                setIsResourceSearchActive(false);
                                                setResourceSearchHasIssuerDetails(false);

                                                if (val === 'department') {
                                                    GetMultipleInstituteStreamsObject();
                                                }

                                                setDepartmentOptions([]);
                                                setShowSections(false);
                                                resetUserState();
                                            }}
                                            styles={{ container: base => ({ ...base, width: '100%' }), menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                            menuPortalTarget={document.body}
                                            menuPosition="fixed"
                                        />
                                    </div>

                                    {/* User ERP ID / Department */}
                                    <div style={{ flex: 1 }}>
                                        {searchRole === 'user' ? (
                                            <>
                                                {/* <label className="vm_custom_body5">User ERP ID</label>
                                                <input
                                                    value={userId}
                                                    onChange={(e) => {
                                                        resetUserState({ preserveAccessionResults: true });
                                                        setIsResourceSearchActive(false);
                                                        setResourceSearchHasIssuerDetails(false);
                                                        setUserId(e.target.value);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleSearchUser(userId);
                                                    }}
                                                    placeholder="Enter User ERP ID"
                                                    style={{
                                                        width: '100%',
                                                        height: 40,
                                                        borderRadius: 6,
                                                        padding: '0 8px',
                                                        border: '1px solid rgb(160, 160, 160)'
                                                    }}
                                                />
                                                <div style={{ marginTop: 6, fontSize: 12, color: '#666', fontStyle: 'italic' }}>
                                                    Provide complete user ID and press Enter to view user details.
                                                </div> */}

                                                {/* suggestion user selection field */}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <label className="vm_custom_body5" style={{ marginBottom: 0 }}>User Details</label>
                                                    <div className='no-print' style={{ position: 'relative', display: 'inline-block' }}
                                                        onMouseEnter={(e) => {
                                                            const tooltip = e.currentTarget.querySelector('.custom-tooltip');
                                                            if (tooltip) tooltip.style.display = 'block';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            const tooltip = e.currentTarget.querySelector('.custom-tooltip');
                                                            if (tooltip) tooltip.style.display = 'none';
                                                        }}>

                                                        <span aria-label="How to Use info">
                                                            <FontAwesomeIcon
                                                                icon={['fas', 'info-circle']}
                                                                size="sm"
                                                                className="info-button"
                                                                style={{ cursor: 'pointer', color: '#4a4a4a' }}
                                                            />
                                                        </span>

                                                        <div
                                                            className="custom-tooltip"
                                                            style={{
                                                                display: 'none',
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: 0,
                                                                background: '#fff',
                                                                color: '#333',
                                                                padding: '10px',
                                                                width: '430px',
                                                                borderRadius: '6px',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                                fontSize: '13px',
                                                                zIndex: 999,
                                                                whiteSpace: 'normal'
                                                            }}
                                                        >
                                                            Search by user ERP ID, First Name, Last Name, Addmission Number, Email Id, Contact Number, PRN, GRN, Employee Code.
                                                        </div>
                                                    </div>
                                                </div>

                                                <div style={{ position: 'relative' }} title={searchByUserDetails || ''}>
                                                    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: 4, minHeight: 42, background: '#fff', padding: '0 8px', boxSizing: 'border-box' }}>
                                                        <input
                                                            ref={userDetailsInputRef}
                                                            type="text"
                                                            value={searchByUserDetails}
                                                            placeholder="Enter or select User Details"
                                                            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, background: 'transparent', minWidth: 0 }}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                setSearchByUserDetails(val);
                                                                if (val.trim()) {
                                                                    debouncedFetchUserSuggestions(val, 1000);
                                                                    setShowUserDetailsDropdown(true);
                                                                } else {
                                                                    setShowUserDetailsDropdown(false);
                                                                    clearUserSearchState();
                                                                }
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    const inputVal = e.currentTarget?.value || searchByUserDetails || '';
                                                                    const q = getUserQueryValue(inputVal);
                                                                    if (q) {
                                                                        setSearchByUserDetails(q);
                                                                        debouncedFetchUserSuggestions(q, 1000);
                                                                        setShowUserDetailsDropdown(true);
                                                                    } else {
                                                                        setShowUserDetailsDropdown(false);
                                                                    }
                                                                    // Keep focus and cursor at end
                                                                    const inp = userDetailsInputRef.current;
                                                                    if (inp) {
                                                                        const len = inp.value.length;
                                                                        requestAnimationFrame(() => {
                                                                            inp.focus();
                                                                            inp.setSelectionRange(len, len);
                                                                        });
                                                                    }
                                                                } else if (e.key === 'Escape') {
                                                                    setShowUserDetailsDropdown(false);
                                                                }
                                                            }}
                                                            onFocus={() => {
                                                                if (searchByUserDetails.trim() && userOptions.length > 0) {
                                                                    setShowUserDetailsDropdown(true);
                                                                }
                                                            }}
                                                            onBlur={() => {
                                                                // Delay hiding so option clicks register first
                                                                setTimeout(() => setShowUserDetailsDropdown(false), 200);
                                                            }}
                                                        />
                                                        {loadingUserData && (
                                                            <span style={{ fontSize: 11, color: '#999', marginLeft: 4, whiteSpace: 'nowrap' }}>Loading...</span>
                                                        )}
                                                        {searchByUserDetails && (
                                                            <span
                                                                onMouseDown={(e) => { e.preventDefault(); clearUserSearchState(); setShowUserDetailsDropdown(false); }}
                                                                style={{ cursor: 'pointer', color: '#999', fontSize: 16, marginLeft: 4, lineHeight: 1 }}
                                                                title="Clear"
                                                            >✕</span>
                                                        )}
                                                    </div>

                                                    {/* Suggestion dropdown */}
                                                    {showUserDetailsDropdown && userOptions.length > 0 && (
                                                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ccc', borderRadius: 4, zIndex: 9999, maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
                                                            {userOptions.map((opt, idx) => (
                                                                <div
                                                                    key={opt.value || idx}
                                                                    onMouseDown={(e) => {
                                                                        e.preventDefault();
                                                                        setShowUserDetailsDropdown(false);
                                                                        const rawLabel = opt.value || '';
                                                                        const userQueryVal = getUserQueryValue(rawLabel);   // change this function name as it's not really for accession in this context, but reusing it since it already has the logic to extract the query value from the input (removing any appended labels from dropdown selection)
                                                                        setSearchByUserDetails(userQueryVal);
                                                                        if (userQueryVal) handleSearchUserData(userQueryVal);   //change this function name as well since it's not really for accession, but it does handle searching for user details when user selects a dropdown option
                                                                    }}
                                                                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f0f0f0' }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                                                                >
                                                                    {opt.label}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <label className="vm_custom_body5">Department</label>
                                                <Select
                                                    options={departmentOptions}
                                                    value={departmentOptions.find(o => o.value === selectedDepartment) || null}
                                                    onChange={(opt) => {
                                                        setSelectedDepartment(opt?.value || '');
                                                        handleDepartmentSelect(opt?.value);
                                                    }}
                                                    placeholder={
                                                        departmentOptions.length
                                                            ? 'Select Department'
                                                            : (isDepartmentLoading ? 'Loading...' : 'No departments')
                                                    }
                                                    isDisabled={departmentOptions.length === 0 || isDepartmentLoading}
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* DIVIDER */}
                            <div
                                style={{
                                    width: '1px',
                                    backgroundColor: '#999',
                                    margin: '0 12px'
                                }}
                            />

                            {/* RIGHT SECTION */}
                            <div style={{ flex: 1, paddingRight: 12 }}>
                                <div style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>
                                    Search by Resource Details
                                </div>

                                {/* How to Use link + info icon */}
                                <div className='no-print' style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                </div>

                                <div style={{ display: 'flex', gap: 12, width: '100%' }}>

                                    <div style={{ flex: 1.7, position: 'relative' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <label className="vm_custom_body5" style={{ marginBottom: 0 }}>Accession Details</label>

                                            {/* info button */}
                                            {/* <div className='no-print' style={{ position: 'relative', display: 'inline-block' }}
                                                onMouseEnter={(e) => {
                                                    const tooltip = e.currentTarget.querySelector('.custom-tooltip');
                                                    if (tooltip) tooltip.style.display = 'block';
                                                }}
                                                onMouseLeave={(e) => {
                                                    const tooltip = e.currentTarget.querySelector('.custom-tooltip');
                                                    if (tooltip) tooltip.style.display = 'none';
                                                }}>

                                                <span aria-label="Accession details search info">
                                                    <FontAwesomeIcon
                                                        icon={['fas', 'info-circle']}
                                                        size="sm"
                                                        className="info-button"
                                                        style={{ cursor: 'pointer', color: '#4a4a4a' }}
                                                    />
                                                </span>

                                                <div
                                                    className="custom-tooltip"
                                                    style={{
                                                        display: 'none',
                                                        position: 'absolute',
                                                        top: '100%',
                                                        left: 0,
                                                        background: '#fff',
                                                        color: '#333',
                                                        padding: '10px',
                                                        width: '400px',
                                                        borderRadius: '6px',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                        fontSize: '13px',
                                                        zIndex: 999,
                                                        whiteSpace: 'normal'
                                                    }}
                                                >
                                                    <strong>Note:</strong><br />
                                                    <b>1 :</b> In the old Issue/Return screen, the barcode was generated using only the accession number.<br />
                                                    <b>2 :</b> For the new Issue/Return screen, the barcode should include both <strong>Accession Series</strong> and <strong>Accession Number</strong> as parameters.<br />
                                                    <b>3 :</b> A new Barcode Template needs to be added specifically from Print Barcode (Template Management).<br />
                                                    <b>4 :</b> The old barcode format will not be applicable or usable on the new Issue/Return page.<br />
                                                    <b>5 :</b> However, the old barcode can still be used in the existing (old) Issue/Return screen.
                                                </div>
                                            </div> */}
                                        </div>

                                        <div style={{ position: 'relative' }} title={searchByAccession || ''}>
                                            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: 4, minHeight: 42, background: '#fff', padding: '0 8px', boxSizing: 'border-box' }}>
                                                <input
                                                    ref={accessionInputRef}
                                                    type="text"
                                                    value={searchByAccession}
                                                    placeholder="Enter or select Accession Details"
                                                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, background: 'transparent', minWidth: 0 }}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setSearchByAccession(val);
                                                        setAccession(null);
                                                        if (val.trim()) {
                                                            debouncedFetchAccessionSuggestions(val, 1000);
                                                            setShowAccessionDropdown(true);
                                                        } else {
                                                            setShowAccessionDropdown(false);
                                                            clearAccessionSearchState();
                                                        }
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            // setShowAccessionDropdown(false);
                                                            const inputVal = e.currentTarget?.value || searchByAccession || '';
                                                            const q = getAccessionQueryValue(inputVal || '');
                                                            if (q) {
                                                                // const matchedOption = (Array.isArray(accessionOptions) ? accessionOptions : []).find((opt) => {
                                                                //     const queryFromOption = getAccessionQueryValue(opt?.value || '');
                                                                //     return String(queryFromOption || '').trim() === String(q || '').trim();
                                                                // });

                                                                // const nextMeta = {
                                                                //     barcode: String(
                                                                //         matchedOption?.barcode
                                                                //         || matchedOption?.raw?.accessionNumber
                                                                //         || matchedOption?.raw?.accessionnumber
                                                                //         || matchedOption?.raw?.accession_number
                                                                //         || ''
                                                                //     ),
                                                                //     accessionSeriesId: String(
                                                                //         matchedOption?.accessionSeriesId
                                                                //         || matchedOption?.raw?.accession_series_id
                                                                //         || matchedOption?.raw?.accessionSeriesId
                                                                //         || matchedOption?.raw?.accessionSeries_id
                                                                //         || ''
                                                                //     ),
                                                                // };

                                                                // setSelectedAccessionMeta(nextMeta);
                                                                setAccessionNumber(q);
                                                                setSearchByAccession(q);
                                                                debouncedFetchAccessionSuggestions(q, 1000);
                                                                setShowAccessionDropdown(true);
                                                                // handleSearchAccession(q, nextMeta);
                                                            } else {
                                                                setShowAccessionDropdown(false);
                                                            }
                                                            // Keep focus and cursor at end
                                                            const inp = accessionInputRef.current;
                                                            if (inp) {
                                                                const len = inp.value.length;
                                                                requestAnimationFrame(() => {
                                                                    inp.focus();
                                                                    inp.setSelectionRange(len, len);
                                                                });
                                                            }
                                                        } else if (e.key === 'Escape') {
                                                            setShowAccessionDropdown(false);
                                                        }
                                                    }}
                                                    onFocus={() => {
                                                        if (searchByAccession.trim() && accessionOptions.length > 0) {
                                                            setShowAccessionDropdown(true);
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        // Delay hiding so option clicks register first
                                                        setTimeout(() => setShowAccessionDropdown(false), 200);
                                                    }}
                                                />
                                                {loadingAccessionData && (
                                                    <span style={{ fontSize: 11, color: '#999', marginLeft: 4, whiteSpace: 'nowrap' }}>Loading...</span>
                                                )}
                                                {searchByAccession && (
                                                    <span
                                                        onMouseDown={(e) => { e.preventDefault(); clearAccessionSearchState(); setShowAccessionDropdown(false); }}
                                                        style={{ cursor: 'pointer', color: '#999', fontSize: 16, marginLeft: 4, lineHeight: 1 }}
                                                        title="Clear"
                                                    >✕</span>
                                                )}
                                            </div>

                                            {/* Suggestion dropdown */}
                                            {showAccessionDropdown && accessionOptions.length > 0 && (
                                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ccc', borderRadius: 4, zIndex: 9999, maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
                                                    {accessionOptions.map((opt, idx) => (
                                                        <div
                                                            key={opt.value || idx}
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                if (opt.disabled) return;
                                                                setShowAccessionDropdown(false);
                                                                setAccession(opt);
                                                                const rawLabel = opt.value || '';
                                                                const accessionVal = getAccessionQueryValue(rawLabel);
                                                                setSearchByAccession(accessionVal);
                                                                setAccessionNumber(accessionVal);
                                                                // store barcode + accessionSeriesId from the raw suggestion item
                                                                const rawItem = opt.raw || {};
                                                                const nextMeta = {
                                                                    barcode: String(opt.barcode || rawItem.accessionNumber || rawItem.accessionnumber || rawItem.accession_number || ""),
                                                                    accessionSeriesId: String(opt.accessionSeriesId || rawItem.accession_series_id || rawItem.accessionSeriesId || rawItem.accessionSeries_id || ""),
                                                                };
                                                                setSelectedAccessionMeta(nextMeta);
                                                                if (accessionVal) handleSearchAccession(accessionVal, nextMeta);
                                                            }}
                                                            style={{ padding: '8px 12px', cursor: opt.disabled ? 'default' : 'pointer', fontSize: 13, borderBottom: '1px solid #f0f0f0', color: opt.disabled ? '#999' : undefined}}
                                                            onMouseEnter={(e) => { if (!opt.disabled) e.currentTarget.style.background = '#f5f5f5'; }}
                                                            onMouseLeave={(e) => { if (!opt.disabled) e.currentTarget.style.background = '#fff'; }}
                                                        >
                                                            {opt.label}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ flex: 1 }}>
                                    </div>

                                    <div style={{ flex: 1 }}>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* Confirmation modal for reservation when issuing resource */}
                <div className={showIssueConfirm ? "modal fade show" : "modal fade"} id="issueConfirmationModal" style={{ display: showIssueConfirm ? 'block' : 'none' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content modalBulkContent">
                            <div className="modal-header" style={{ padding: '20px 25px', backgroundColor: '#c34141', color: ' #ffffff' }}>
                                <h6 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <FontAwesomeIcon size='sm' icon={["fa", "exclamation-triangle"]} style={{ marginRight: '10px' }} />
                                    Reservation Confirmation
                                </h6>
                                <FontAwesomeIcon icon={['fas', 'times']} className="text-white" size="sm" onClick={() => { setShowIssueConfirm(false); }} style={{ cursor: "pointer" }} />
                            </div>

                            <div className="custom-form-body" style={{ padding: '20px 25px' }}>
                                <div id="issueConfirmationMessage" style={{ marginBottom: 12 }}>{issueConfirmationMessage}</div>
                                <div id="issueConfirmationQuestion" style={{ fontWeight: 600 }}>{issueConfirmationQuestion}</div>
                            </div>

                            <div className="modal-footer" style={{ padding: '20px 25px' }}>
                                <button
                                    className="btn delete-primary-button"
                                    style={{ fontSize: '14px', borderRadius: '7px' }}
                                    onClick={() => setShowIssueConfirm(false)}
                                >
                                    No
                                </button>
                                <button className="btn delete-outline-button waves-effect" style={{ fontSize: '14px', borderRadius: '7px' }}
                                    onClick={() => proceedIssuedResource()} disabled={isIssuerLoading}>
                                    Yes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2/3 and action footer: show only after a search is started */}

                {scannedUserMessage ? (
                    <div style={{ padding: 12, background: '#fff3f3', border: '1px solid #f5c2c2', color: '#a33', borderRadius: 6, margin: '8px 0' }}>
                        {scannedUserMessage}
                    </div>
                ) : null}

                {showSections && (
                    <>
                        {/* Section 2: User details (shown only for user search role) */}
                        {shouldShowUserDetailsSection && (
                            <div style={{ marginBottom: 16 }}>
                                <h5 style={{ margin: '6px 0' }}>User Details</h5>

                                {isUserLoading ? (
                                    <div style={{ padding: 18, display: 'flex', justifyContent: 'center' }}>
                                        <ButtonLoader />
                                    </div>
                                ) : (
                                    <div style={{ background: '#fff', border: '1px solid rgb(160, 160, 160)', borderRadius: 8, padding: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', width: '100%' }}>

                                                {/* Profile Image */}
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flex: '0 0 140px' }}>
                                                    <div style={{ width: 88, height: 88, borderRadius: 8, background: '#f4f6f8', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                        {userDetails.photo ? (
                                                            <img
                                                                src={userDetails.photo}
                                                                alt="profile"
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div style={{ width: 72, height: 72, borderRadius: 8, background: '#dfe6ee', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#334' }}>
                                                                <span style={{ fontSize: 18, fontWeight: 700 }}>
                                                                    {(userDetails.name || 'U')
                                                                        .split(' ')
                                                                        .map(n => n[0])
                                                                        .slice(0, 2)
                                                                        .join('')
                                                                        .toUpperCase()}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* User Info */}
                                                <div style={{ flex: 1, paddingLeft: 8 }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

                                                        {/* Row 1 — fields common to all roles */}
                                                        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                                                            <div style={{ minWidth: 250 }}>
                                                                <div style={{ fontSize: 12, color: '#666' }}>User ERP ID</div>
                                                                <div>{userDetails.erpId || '-'}</div>
                                                            </div>

                                                            <div style={{ minWidth: 250 }}>
                                                                <div style={{ fontSize: 12, color: '#666' }}>Name</div>
                                                                <div style={{ fontWeight: 600 }}>{userDetails.name || '-'}</div>
                                                            </div>

                                                            <div style={{ minWidth: 150 }}>
                                                                <div style={{ fontSize: 12, color: '#666' }}>Issued Resource</div>
                                                                <div>{userDetails.issuedResources ?? '-'}</div>
                                                            </div>

                                                            {/* Student-only: Admission Number */}
                                                            {userDetails.userRole === 'student' && (
                                                                <div style={{ minWidth: 150 }}>
                                                                    <div style={{ fontSize: 12, color: '#666' }}>Admission Number</div>
                                                                    <div>{userDetails.admissionNumber || '-'}</div>
                                                                </div>
                                                            )}

                                                            {userDetails.userRole === 'student' && (
                                                                <div style={{ minWidth: 150 }}>
                                                                    <div style={{ fontSize: 12, color: '#666' }}>Roll Number</div>
                                                                    <div>{userDetails.rollNumber || '-'}</div>
                                                                </div>
                                                            )}

                                                            {/* Faculty/Employee-only: Employee ID */}
                                                            {userDetails.userRole !== 'student' && userDetails.userRole !== '' && (
                                                                <div style={{ minWidth: 150 }}>
                                                                    <div style={{ fontSize: 12, color: '#666' }}>Employee ID</div>
                                                                    <div>{userDetails.employeeId || '-'}</div>
                                                                </div>
                                                            )}

                                                            <div style={{ minWidth: 350 }}>
                                                                <div style={{ fontSize: 12, color: '#666' }}>Email</div>
                                                                <div>{userDetails.email || '-'}</div>
                                                            </div>
                                                        </div>

                                                        {/* Row 2 */}
                                                        <div style={{ marginTop: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                                            <div style={{ minWidth: 250 }}>
                                                                <div style={{ fontSize: 12, color: '#666' }}>Contact Number</div>
                                                                <div>{userDetails.mobileNo || '-'}</div>
                                                            </div>

                                                            <div style={{ minWidth: 250 }}>
                                                                <div style={{ fontSize: 12, color: '#666' }}>Membership</div>
                                                                <div style={{ fontSize: 14, color: membershipStatus === 'Active' ? 'green' : (membershipStatus === 'Expired' ? 'red' : 'black') }}>
                                                                    {membershipStatus !== 'NA' ? membershipStatus : 'NA'}
                                                                </div>
                                                            </div>

                                                            {/* Student-only: Stream Name, Group Name and Academic Year */}
                                                            {userDetails.userRole === 'student' && (
                                                                <>
                                                                    <div style={{ minWidth: 250 }}>
                                                                        <div style={{ fontSize: 12, color: '#666' }}>Stream Name</div>
                                                                        <div>{streamName || '-'}</div>
                                                                    </div>

                                                                    <div style={{ minWidth: 250 }}>
                                                                        <div style={{ fontSize: 12, color: '#666' }}>Group Name</div>
                                                                        <div>{groupName || '-'}</div>
                                                                    </div>

                                                                    <div style={{ minWidth: 250 }}>
                                                                        <div style={{ fontSize: 12, color: '#666' }}>Academic Year</div>
                                                                        <div>{academicYear || '-'}</div>
                                                                    </div>
                                                                </>
                                                            )}

                                                            <div style={{ minWidth: 250 }}>
                                                                <div style={{ fontSize: 12, color: '#666' }}>Access</div>
                                                                <div style={{ fontSize: 14, color: (userAccess === 'Open' || userAccess === 'open' ? 'green' : (userAccess === 'Blocked' || userAccess === 'blocked' ? 'red' : 'black')) }}>
                                                                    {String(userAccess).charAt(0).toUpperCase() + String(userAccess).slice(1) || 'NA'}
                                                                </div>
                                                            </div>
                                                        </div>

                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Section 3: Issued resources table */}
                        <div>
                            <h5 style={{ margin: '6px 0' }}>Resources List</h5>

                            {/* Display resource availability message when resource not found or not available */}
                            {resourceAvailabilityMessage && (
                                <div
                                    style={{
                                        backgroundColor: '#fff3cd',
                                        border: '1px solid #ffc107',
                                        color: '#856404',
                                        padding: '12px 16px',
                                        borderRadius: '4px',
                                        marginBottom: '12px',
                                        fontSize: '14px',
                                    }}
                                >
                                    {resourceAvailabilityMessage}
                                </div>
                            )}

                            {isUserAccessBlocked && departmentAccessMessage ? (
                                <div style={{ padding: 12, background: '#fff3f3', border: '1px solid #f5c2c2', color: '#a33', borderRadius: 6, margin: '8px 0' }}>
                                    {departmentAccessMessage}
                                </div>
                            ) : null}

                            {/* Action footer: show only when department access is not explicitly denied AND at least one issued resource is selected */}
                            {/* {departmentAccessVerified !== false && selectedIssuedIds && selectedIssuedIds.length > 0 && ( */}
                            <div
                                style={{
                                    position: 'sticky',
                                    bottom: 0,
                                    background: '#fff',
                                    padding: '12px 0',
                                    marginTop: 16,
                                    display: 'flex',
                                    gap: 8,
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    borderTop: '1px solid #eee',
                                    zIndex: 20,
                                }}
                            >
                                {issuedList.length > 0 && (
                                    <>
                                        <button className="uploadButton" style={{ minWidth: 140 }} disabled={hasNoDueCertificate || isUserAccessBlocked} onClick={() => validateIssuedResourceSubmission()}>Issue</button>
                                        <button className="uploadButton" style={{ minWidth: 140 }} onClick={() => handleReturnRenew('return')}>Return</button>
                                        <button className="uploadButton" style={{ minWidth: 140 }} onClick={() => handleReturnRenew('renew')}>Renewal</button>
                                    </>
                                )}
                                {searchRole !== 'department' && (
                                    <button
                                        className="uploadButton"
                                        style={{ minWidth: 200 }}
                                        onClick={() => { if (!disableNoDueCertificate) setShowNoDueConfirm(true); }}
                                        disabled={disableNoDueCertificate}
                                        aria-disabled={disableNoDueCertificate}
                                    >
                                        Issue No due certificate
                                    </button>
                                )}
                            </div>

                            {/* If department access verification failed, show message and hide table/footer */}
                            {departmentAccessVerified === false ? (
                                <div style={{ padding: 12, background: '#fff3f3', border: '1px solid #f5c2c2', color: '#a33', borderRadius: 6, margin: '8px 0' }}>
                                    {departmentAccessMessage || `Your assigned branch/department in the library does not match the selected issuer's default branch.`}
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                                        <table className="table table-bordered vm_libraryReport_table" style={{ minWidth: 1100 }}>
                                            <thead style={{ backgroundColor: '#f2f3f4' }}>
                                                <tr>
                                                    <th style={{ width: 40 }}>
                                                        <input
                                                            type="checkbox"
                                                            aria-label="Select all issued resources"
                                                            checked={issuedList.length > 0 && selectedIssuedIds.length === issuedList.length}
                                                            onChange={() => {
                                                                if (issuedList.length > 0 && selectedIssuedIds.length === issuedList.length) {
                                                                    setSelectedIssuedIds([]);
                                                                } else {
                                                                    setSelectedIssuedIds(issuedList.map(r => r.id));
                                                                }
                                                            }}
                                                        />
                                                    </th>
                                                    <th>Accession Details</th>
                                                    <th>Book Status</th>
                                                    <th>Title</th>
                                                    <th>Issue Date</th>
                                                    <th>Issued Policy</th>
                                                    <th>Due Date</th>
                                                    <th>Type</th>
                                                    <th>Late fine</th>
                                                    <th>Resource condition</th>
                                                    <th>Damaged/Lost Fine</th>
                                                    <th>Total Fine</th>
                                                    <th>Collected Fine</th>
                                                    <th>Receipt Number</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(isIssuerLoading) ? (
                                                    <tr>
                                                        <td colSpan="13" style={{ textAlign: 'center' }}>
                                                            <ButtonLoader />
                                                        </td>
                                                    </tr>
                                                ) : issuedList.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="13" style={{ textAlign: 'center' }}>
                                                            No records found.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    issuedList.map((row, i) => (
                                                        <tr key={row.id || i} style={{ backgroundColor: row.isBookIssued ? '#7a7a7a' : undefined, color: row.isBookIssued ? '#ffffff' : (isRowOverdue(row) ? 'red' : undefined) }}>
                                                            <td style={{ textAlign: 'center' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedIssuedIds.includes(row.id)}
                                                                    onChange={() => {
                                                                        if (selectedIssuedIds.includes(row.id)) {
                                                                            setSelectedIssuedIds(prev => prev.filter(x => x !== row.id));
                                                                        } else {
                                                                            setSelectedIssuedIds(prev => [...prev, row.id]);
                                                                        }
                                                                    }}
                                                                />
                                                            </td>
                                                            <td>
                                                                {((row.accessionSeries === undefined && !row.accessionDetails) || (row.accessionSeries === '' || row.accessionSeries == null) || (row.accessionNumber === '' || row.accessionNumber == null)) ? (
                                                                    // when series/number missing, show empty string or fallback
                                                                    row.accessionDetails && row.accessionDetails !== '-' ? row.accessionDetails : ''
                                                                ) : (
                                                                    <div>
                                                                        <div>{`${row.accessionSeries}-${row.accessionNumber}`}</div>
                                                                        {row.resourceInstituteName ? <div style={{ fontSize: 12 }}>{row.resourceInstituteName}</div> : null}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>{row.bookStatus ? String(row.bookStatus).charAt(0).toUpperCase() + String(row.bookStatus).slice(1) : ''}</td>
                                                            <td title={row.title || ''} style={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {(row.title && row.title.length > 20) ? `${row.title.slice(0, 20)}...`.replace(/\u00A0/g, ' ') : row.title}
                                                                {(!row.title || row.title === '') ? '-' : null}
                                                            </td>
                                                            <td>{formatIssueDateForDisplay(row.issueDate)}</td>
                                                            <td>
                                                                {(() => {
                                                                    const roleKeyRaw = String((row.selectedUserRole || '').toString()).toLowerCase();
                                                                    const rowPolicyIid = normalizeIidValue(
                                                                        row.userInstituteId
                                                                        || row.resourceInstituteId
                                                                        || issuerStudentIID
                                                                        || ((selectedInstitute === 'all') ? config.institute_id : selectedInstitute)
                                                                        || config.institute_id
                                                                    );
                                                                    const rolePolicyOptions = buildPolicyOptions(roleKeyRaw, rowPolicyIid);

                                                                    const localOptions = (row.policyOptions && row.policyOptions.length) ? row.policyOptions : rolePolicyOptions;
                                                                    const currentPolicyId = String(row.policy_id || row.selectedPolicyId || '');

                                                                    const bookStatus = String(row.bookStatus || '').toLowerCase();

                                                                    const cellStyle = { backgroundColor: row.isBookIssued ? '#7a7a7a' : undefined, color: row.isBookIssued ? '#ffffff' : (isRowOverdue(row) ? 'red' : undefined) };

                                                                    // Issued: show non-editable policy name (lookup by policy_id)
                                                                    if (bookStatus === 'issued' || row.isBookIssued) {
                                                                        const found = localOptions.find(o => String(o.value) === currentPolicyId);
                                                                        const label = found
                                                                            ? found.label
                                                                            : (currentPolicyId
                                                                                ? getPolicyLabelById(currentPolicyId, row.selectedUserRole || row.issuerRole || currentUserRole, rowPolicyIid)
                                                                                : 'No policy');
                                                                        const display = (label && label.length > 20) ? `${label.slice(0, 20)}...` : label;
                                                                        return <span style={cellStyle} title={label || ''}>{display}</span>;
                                                                    }

                                                                    // Available: show select dropdown filtered by the searched user role
                                                                    if (bookStatus === 'available') {
                                                                        return (localOptions && localOptions.length) ? (
                                                                            <>
                                                                                <select
                                                                                    value={row.selectedPolicyId || currentPolicyId}
                                                                                    onChange={async (e) => {
                                                                                        const newPolicy = e.target.value;
                                                                                        const nextRows = issuedList.map(it => {
                                                                                            if (it.id !== row.id) return it;
                                                                                            return { ...it, selectedPolicyId: newPolicy, policy_id: newPolicy };
                                                                                        });

                                                                                        setIssuedList(nextRows);
                                                                                        validatePolicyIssueLimits(nextRows, selectedIssuedIds, { syncState: true });

                                                                                        try {
                                                                                            const resourceCopyIdForCalc = row.resourceCopyId || row.id || '';
                                                                                            if (newPolicy && resourceCopyIdForCalc) {
                                                                                                await getPolicyWiseDueDate(newPolicy, resourceCopyIdForCalc);
                                                                                            }
                                                                                        } catch (err) {
                                                                                            console.error('Error calculating due date for selected policy:', err);
                                                                                        }
                                                                                    }}
                                                                                    title={(localOptions.find(o => String(o.value) === (row.selectedPolicyId || currentPolicyId)) || {}).label || ''}
                                                                                    style={{ ...cellStyle, width: 220, padding: '4px 6px', borderRadius: 4 }}
                                                                                >
                                                                                    {localOptions.map(opt => (
                                                                                        <option key={opt.value} value={opt.value} title={opt.label}>{(opt.label && opt.label.length > 20) ? `${opt.label.slice(0, 20)}...` : opt.label}</option>
                                                                                    ))}
                                                                                </select>
                                                                                {policyLimitErrors[String(row.id)] ? (
                                                                                    <div style={{ marginTop: 4, color: '#dc3545', fontSize: 12 }}>{policyLimitErrors[String(row.id)]}</div>
                                                                                ) : null}
                                                                            </>
                                                                        ) : (
                                                                            <span style={cellStyle} title={''}>No policy</span>
                                                                        );
                                                                    }

                                                                    // Fallback: show label or no policy
                                                                    if (localOptions && localOptions.length) {
                                                                        const found = localOptions.find(o => String(o.value) === currentPolicyId);
                                                                        const label = found
                                                                            ? found.label
                                                                            : (currentPolicyId
                                                                                ? getPolicyLabelById(currentPolicyId, row.selectedUserRole || row.issuerRole || currentUserRole, rowPolicyIid)
                                                                                : 'No policy');
                                                                        const display = (label && label.length > 20) ? `${label.slice(0, 20)}...` : label;
                                                                        return <span style={cellStyle} title={label || ''}>{display}</span>;
                                                                    }

                                                                    return <span style={cellStyle} title={''}>No policy</span>;
                                                                })()}
                                                            </td>
                                                            <td>
                                                                {String(row.bookStatus || '').toLowerCase() === 'available'
                                                                    ? (policyDueDates[String(row.resourceCopyId || row.id)] || row.calculatedDueDate || row.dueDate || '')
                                                                    : (row.dueDate || row.calculatedDueDate || '')}
                                                            </td>
                                                            <td>{row.type ? String(row.type).charAt(0).toUpperCase() + String(row.type).slice(1) : ''}</td>
                                                            <td id={`lateFine${row.id}`}>
                                                                {String(row.selectedUserRole || '').toLowerCase() === 'department'
                                                                    ? 'NA'
                                                                    : (typeof row.lateFine === 'number' ? row.lateFine.toFixed(2) : row.lateFine)}
                                                            </td>
                                                            <td>
                                                                {String(row.selectedUserRole || '').toLowerCase() === 'department' ? (
                                                                    <span>NA</span>
                                                                ) : String(row.bookStatus || '').toLowerCase() === 'available' ? (
                                                                    <span style={{ color: isRowOverdue(row) ? 'red' : undefined }}>Good</span>
                                                                ) : (
                                                                    <select
                                                                        value={row.resourceCondition || 'Good'}
                                                                        onChange={(e) => updateResourceCondition(row.id, e.target.value)}
                                                                        style={{ width: 180, padding: '4px 6px', borderRadius: 4, borderColor: isRowOverdue(row) ? 'red' : undefined, color: isRowOverdue(row) ? 'red' : undefined }}
                                                                    >
                                                                        <option value="Good">Good</option>
                                                                        <option value="Partially Damaged">Partially Damaged</option>
                                                                        <option value="Resource Lost / Missing">Resource Lost / Missing</option>
                                                                    </select>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {String(row.selectedUserRole || '').toLowerCase() === 'department' ? (
                                                                    <span>NA</span>
                                                                ) : (
                                                                    <input
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        id={`damagedFine_${row.id}`}
                                                                        placeholder="0"
                                                                        value={row.damagedLostFine != null ? String(row.damagedLostFine) : ''}
                                                                        onChange={(e) => handleDamagedInputChange(row.id, e.target.value)}
                                                                        style={{ ...inputBaseStyle, width: 120, padding: '4px 6px', color: isRowOverdue(row) ? 'red' : undefined, borderBottom: isRowOverdue(row) ? '1px solid red' : inputBaseStyle.borderBottom }}
                                                                        disabled={String(row.bookStatus || '').toLowerCase() === 'available'}
                                                                    />
                                                                )}
                                                            </td>
                                                            <td id={`totalFine${row.id}`}>
                                                                {String(row.selectedUserRole || '').toLowerCase() === 'department'
                                                                    ? 'NA'
                                                                    : (typeof row.totalFine === 'number' ? row.totalFine.toFixed(2) : row.totalFine)}
                                                            </td>
                                                            <td>
                                                                {String(row.selectedUserRole || '').toLowerCase() === 'department' ? (
                                                                    <span>NA</span>
                                                                ) : (
                                                                    <>
                                                                        <input
                                                                            type="text"
                                                                            inputMode="decimal"
                                                                            id={`collectedFine_${row.id}`}
                                                                            placeholder="0"
                                                                            value={row.collectedFine != null ? String(row.collectedFine) : ''}
                                                                            onChange={(e) => handleCollectedInputChange(row.id, e.target.value)}
                                                                            style={{ ...inputBaseStyle, width: 120, padding: '4px 6px', color: isRowOverdue(row) ? 'red' : undefined, borderBottom: isRowOverdue(row) ? '1px solid red' : inputBaseStyle.borderBottom }}
                                                                            disabled={String(row.bookStatus || '').toLowerCase() === 'available'}
                                                                        />
                                                                        <div id={`collectedFineErrorMessage_${row.id}`} style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                                                                            {collectedFineErrors[row.id] || ''}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="text"
                                                                    id={`receiptNumber_${row.id}`}
                                                                    maxLength={250}
                                                                    value={row.receiptNumber || ''}
                                                                    onChange={(e) => handleReceiptChange(row.id, e.target.value)}
                                                                    placeholder="Enter Receipt Number"
                                                                    style={{ ...inputBaseStyle, width: 140, padding: '4px 6px', color: isRowOverdue(row) ? 'red' : undefined, borderBottom: isRowOverdue(row) ? '1px solid red' : inputBaseStyle.borderBottom }}
                                                                    disabled={String(row.bookStatus || '').toLowerCase() === 'available'}
                                                                />
                                                            </td>
                                                            <td>
                                                                {row.isBookIssued ? (
                                                                    <>
                                                                        <div style={{ padding: '6px 8px', minWidth: 220 }}>
                                                                            {`Resource is already issued by ${row.issuerName || (row.raw && (row.raw.issuerName || row.raw.issuer)) || ''}.`}
                                                                        </div>
                                                                        <button
                                                                            type="button"
                                                                            title="Remove"
                                                                            onClick={() => removeIssuedEntry(row.id)}
                                                                            style={{ background: 'transparent', border: 'none', color: undefined, cursor: 'pointer', fontSize: 16, marginLeft: 8 }}
                                                                        >
                                                                            ✖
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <div />
                                                                )}
                                                                {String(row.bookStatus || '').toLowerCase() === 'available' && (
                                                                    <button
                                                                        type="button"
                                                                        title="Remove"
                                                                        onClick={() => removeIssuedEntry(row.id)}
                                                                        style={{ background: 'transparent', border: 'none', color: undefined, cursor: 'pointer', fontSize: 16, marginLeft: 8 }}
                                                                    >
                                                                        ✖
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Action buttons: Issue/Return/Renewal/Issue No due certificate/Cancel - sticky at bottom */}
                        {/* No-Due Certificate warning banner */}
                        {hasNoDueCertificate ? (
                            <div id="alreadyIssueNoDue" style={{ padding: 12, background: '#fff3f3', border: '1px solid #f5c2c2', color: '#a33', borderRadius: 6, margin: '8px 0' }}>
                                No Due Certificate has already been issued on "{noDueIssuedDate}". To issue any resources, please inactive this user from the No Due Certificate screen.
                            </div>
                        ) : null}
                    </>
                )}

                {/* Confirmation modal for issuing No Due certificate (bootstrap-style) */}
                <div className={showNoDueConfirm ? "modal fade show" : "modal fade"} id="modalContainer" style={{ display: showNoDueConfirm ? 'block' : 'none' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content modalBulkContent">
                            {/* Modal Header */}
                            <div className="modal-header" style={{ padding: '20px 25px', backgroundColor: '#c34141', color: ' #ffffff' }}>
                                <h6 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <FontAwesomeIcon size='sm' icon={["fa", "exclamation-triangle"]} style={{ marginRight: '10px' }} />
                                    Confirm No Due Certificate
                                </h6>
                                <FontAwesomeIcon icon={['fas', 'times']} className="text-white" size="sm" onClick={() => { setShowNoDueConfirm(false); }} style={{ cursor: "pointer" }} />
                            </div>

                            <div className="custom-form-body" style={{ padding: '20px 25px' }}>
                                <strong>NOTE:</strong>
                                <ul>
                                    <li>Once a No Due certificate is issued, no resources can be issued to this user.</li>
                                </ul>
                            </div>

                            {/* Modal Footer */}
                            <div className="modal-footer" style={{ padding: '20px 25px' }}>
                                <button
                                    className="btn delete-primary-button"
                                    style={{ fontSize: '14px', borderRadius: '7px' }}
                                    onClick={() => setShowNoDueConfirm(false)}
                                >
                                    No
                                </button>
                                <button className="btn delete-outline-button waves-effect" style={{ fontSize: '14px', borderRadius: '7px' }}
                                    onClick={() => addNoDueCertificate()} disabled={isIssuerLoading}>
                                    Yes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Note about No-Due Certificate actions shown below the action buttons */}
                {/* <div style={{ marginTop: 8, fontSize: 13, color: '#555' }}>
                    (NOTE: If you want to issue any resources after a No Due certificate has been issued, you need to inactive the user from the No Due certificate screen.)
                </div> */}

                {/* render SuccessModal when triggered */}
                {successModal && (
                    <SuccessModal
                        successModal={successModal}
                        successModalType={successModalType}
                        successMessage={successMessage}
                        closeSuccessModal={closeSuccessModal}
                    />
                )}
            </div>
        </div>
    );
};